/**
 * ONE JOB, WHOLE.
 *
 * The car, the money, who said yes to it, and what has happened — on one
 * screen, because the question an advisor actually has is "where is this
 * up to" and answering it used to mean three tabs.
 *
 * The authorization block is the part worth being careful about. It shows
 * a PHONE authorization as a real authorization, because it is one: the
 * law asks for the customer's agreement against a written estimate, not
 * for ink. The system this replaces tested for a signature image and
 * nothing else, so a job agreed on the phone read as unauthorized for ever
 * and staff were asked to fix it with a form that could not.
 */

import type { LineItem } from "../data/jobs.ts";
import { notesCard } from "./notes.ts";
import { setNoCharge, meAsTechnician, myTechnicianId, shopAddress, shopReviewLink, shopTakesCard, shopTrade } from "../data/jobs.ts";
import { getJob, listLines, listAuthorizations, listEvents, updateJobField, setAcReading,
  updateVehicleField, shopLabourRate, setServiceAt, attachVehicle,
  jobTechnicians, setJobTechnicians, ensurePortalToken,
  setStatus, setScheduledAt, STATUS, STATUS_LABEL, stateLabel, vehicleName, whereLabel,
  type Status, type JobRow } from "../data/jobs.ts";
import { technicians } from "../data/pay.ts";
import { icon } from "../ui/icons.ts";
import { canEdit, whyNot, canOverrideProtocol, overrideHint, mayCancel,
  type Role, type Field } from "../domain/permissions.ts";
import { editable, bindEditables } from "../ui/editable.ts";
import { suggestAddress } from "../ui/lookup.ts";
import { foldCards } from "../ui/fold.ts";
import { nextStep, whatBlocks, TRACK, milestones, starsEarned } from "../domain/next-step.ts";
import { authForm, teardownForm } from "./authorizations.ts";
import { mountEsign } from "./esign-card.ts";
import { agreedTeardown, teardownOf, disclosureLines, TEARDOWN_TITLE } from "../domain/teardown.ts";
import { readingsOf, acLines, acNag, anyReading } from "../domain/acreadings.ts";
import { whoBarHtml, advisors, setAdvisor } from "../ui/whobar.ts";
import { isPrePurchase, inSoCal, prePurchaseTiming, prePurchaseNote }
  from "../domain/prepurchase.ts";
import { jobPhotos } from "./photos.ts";
import { techPathOn } from "./tech.ts";
import { factsFrom } from "./advisor.ts";
import { summaryOf, tracksFor, holdsBoth, type TrackKey } from "../domain/tracks.ts";
import { carPhrase } from "../domain/carphrase.ts";
import { words, cap } from "../domain/trade.ts";
import { sendTheQuote, type SendVia } from "../data/sendquote.ts";
import { isVisitOnly, yesCovers } from "../domain/visitquote.ts";
import { fencedTests, humanNotes, diagnosisOf, hasDiagnosis } from "../data/tests.ts";
import { customerComms } from "./comms.ts";
import { trackingLineFor } from "../data/comms.ts";
import { moneyFor, recordPayment, raiseInvoice, raiseDepositInvoice, setInvoiceTotal, voidPayment, billedElsewhere, closedByPrepayment, METHOD_LABEL, type Method } from "../data/payments.ts";
import { isPpiJob, offersPrepay, prepayLabel, ppiPayText, advisorLine } from "../domain/ppi.ts";
import { claimPpi, claimOf, maySendToPpiDesk, wireSendToPpiDesk } from "../data/ppi.ts";
import { lineEditor, loadTiers } from "./lines.ts";
import type { Tier } from "../domain/pricing.ts";
import { api, db, configNow, customerSite } from "../data/client.ts";
import { updateCustomerField } from "../data/customers.ts";
import { reviewForShop } from "../domain/review-links.ts";
import { mountLossFile } from "./restoration.ts";

/**
 * The shop's own review page. For Supercanic, the listing nearest this
 * customer: a review on the wrong market's page helps the wrong market, so
 * that reads the line they rang first and only falls back to their area
 * code. Any other shop gets its own link or none. See reviewForShop().
 */
let rangLine: string | null = null;
let reviewShop: { id: string; review_url: string | null } | null = null;

function reviewPick(job: { customers?: { phone?: string | null } | null }) {
  let overrides: Record<string, string> = {};
  try { overrides = configNow().reviewLinks ?? {}; } catch { /* before boot */ }
  return reviewForShop(reviewShop, {
    trackingNumber: rangLine,
    customerPhone: job.customers?.phone ?? null,
  }, overrides);
}

/** Hidden where the deployment cannot text, so no button lies. */
const canText = (): boolean => {
  try { return configNow().features.sms; } catch { return false; }
};
import { esc, money, ago, mount, firstSentence, since } from "../ui/dom.ts";
import { vinOutHtml } from "../ui/vinout.ts";
import { shopWords, shopName, currentTrade } from "../ui/words.ts";
import { shopStamp, shopWallValue } from "../ui/shopclock.ts";
import { whereItWasDone } from "../domain/whereitwasdone.ts";
import { manualsHref, manualsBlurb } from "../domain/manuals.ts";
import { separatePapersOn, linesFor } from "../domain/billas.ts";
import { MORE_TIME_NOTE } from "../domain/casequote.ts";

/**
 * <input type="datetime-local"> speaks local wall time with no zone on it.
 * Doing this conversion inline is how an appointment ends up seven hours
 * out, so it happens here and nowhere else. The shop's wall clock, not the
 * browser's: see shopWallValue.
 */
const localValue = (iso: string | null): string => shopWallValue(iso);

/**
 * A ONE-LINE NAME FOR THE JOB, when nobody has written one.
 *
 * Quotes were arriving with a blank title — the field is optional and the
 * lead's own `service_wanted` is often empty — so the top of the card said
 * nothing at all about what the work was. The customer's own words are
 * the best description of the fault there is, so the first sentence of
 * the complaint becomes the heading until somebody types a better one.
 *
 * It is a SUGGESTION shown in an editable field, not a value written to
 * the database: a title nobody chose should not become a title nobody can
 * tell apart from one somebody did.
 */
function suggestTitle(job: JobRow): string {
  const said = String(job.complaint ?? "").trim();
  if (!said) return "";
  const first = firstSentence(said) || said;
  return first.length > 90 ? `${first.slice(0, 87).trimEnd()}…` : first;
}

/**
 * WHAT WE ARE ACTUALLY GOING TO DO, IN ONE LINE.
 *
 * Anthony: "We used to have a really good auto made summary of the repair
 * we were going to do on top."
 *
 * The title is the customer's own words -- "grinding when I brake" -- and
 * the lines are the shop's answer to them. Neither alone says what this job
 * IS. Somebody opening it wants the answer first: the work, the car, the
 * money.
 *
 * Built from the LABOR lines, because that is the work. Parts are counted
 * rather than listed -- eight part numbers is not a summary, it is the Work
 * tab. A job with nothing priced yet says so plainly rather than showing an
 * empty summary that looks like a real one.
 */
function repairSummary(job: JobRow, lines: LineItem[], part = "part", partsWord = "parts"): {
  what: string; bits: string[]; priced: boolean;
} {
  const labour = lines.filter((l) => l.kind === "labour" && String(l.description || "").trim());
  const parts = lines.filter((l) => l.kind === "part");
  const names = labour.map((l) => String(l.description).trim());

  const what =
    names.length === 0 ? ""
    : names.length === 1 ? names[0]!
    : names.length === 2 ? `${names[0]} and ${names[1]}`
    : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;

  const hours = labour.reduce((n, l) => n + Number(l.quantity ?? 0), 0);
  const bits: string[] = [];
  if (hours > 0 && hours < 100) {
    bits.push(`${Number(hours.toFixed(2))} ${hours === 1 ? "hr" : "hrs"} labor`);
  }
  /* The trade's word: NYIA read "1 part" for a vehicle fee, 2026-10-14. */
  if (parts.length) bits.push(`${parts.length} ${parts.length === 1 ? part : partsWord}`);

  return {
    what: what || String(job.complaint ?? "").replace(/\s+/g, " ").trim().slice(0, 90),
    bits,
    priced: labour.length > 0 || parts.length > 0,
  };
}

/**
 * THE TABS, LEFT TO RIGHT, IN THE ORDER A JOB GOES THROUGH.
 *
 * The work first because that is where a price comes from, then the money
 * it turns into, then who and where, then what was said, then what has
 * already happened. Five is as many as fits on a phone without scrolling
 * the bar itself, which is why "notes" lives under Details rather than
 * getting a sixth.
 */
/**
 * THE ORDER ANTHONY ASKED FOR, and it is the order somebody reads a job
 * in: who and what first, then what we are doing about it, then the
 * money. Comms and History are the second row because they are what you
 * go looking for rather than what you land on.
 *
 * Three across and two underneath — the strip wraps rather than scrolls,
 * so a tab nobody can see is a tab nobody presses.
 */
/**
 * THE CAR GETS ITS OWN TAB.
 *
 * Anthony: "Nina says she can't edit vehicle information... make sure it
 * is able to be edited by all for year make model, vin, plate and
 * odometer. If it lives elsewhere don't duplicate it."
 *
 * She was right and it was not a permission problem. Plate, VIN and
 * odometer were editable on Details; YEAR, MAKE AND MODEL were printed as
 * a sentence with nothing to click. So the three fields that decide what
 * the car IS -- and therefore what every price on the job comes off --
 * were the only ones nobody could correct.
 *
 * All six live here now and NOWHERE ELSE. Details keeps the car's name as
 * a line of text with a link to this tab, because two places to edit a
 * plate is two places to disagree about it.
 */
const TABS = [
  { key: "details", label: "Details" },
  { key: "work", label: "The work" },
  { key: "vehicle", label: "Vehicle" },
  { key: "money", label: "Money" },
  { key: "comms", label: "Comms" },
  { key: "history", label: "History" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

/**
 * WHICH TAB OPENS, decided by where the job is.
 *
 * A quote opens on the work, because it has no price yet. A job under way
 * opens on the work too — that is where the photos and the sheet are. A
 * finished one opens on the money, because billing it is the only thing
 * left. That is "ordered by step" without the furniture rearranging
 * itself under somebody who has learned where things are.
 */
/**
 * DETAILS, WHATEVER THE JOB IS DOING.
 *
 * This used to open somewhere different for each status, on the reasoning
 * that a finished job is about the money and a quote is about the work.
 * Anthony: "I want the Details tab to be the one that autoloads."
 *
 * He is right, and the reason is that a person opening a job card is
 * almost always checking WHO and WHAT before anything else — whose car,
 * which car, where it is going. Landing somewhere that depends on a status
 * you have not read yet means the first thing you do on every job is work
 * out which screen you are on.
 */
function defaultTab(status: Status, role?: Role): TabKey {
  /*
   * The comment above the tab strip has described this since it was
   * written: "A quote opens on the work, because it needs a price. A job
   * under way opens on the work too. A finished one opens on the money."
   * The function returned "details" whatever happened, so none of it was
   * ever true. Anthony: "Load details out of the six card as the chosen
   * option that loads out for the mechanic as well."
   *
   * A TECHNICIAN ALWAYS OPENS ON THE WORK. Details is the customer's
   * phone number, their email and their address, which is the desk's
   * screen. Somebody standing at the car wants the lines, the photos and
   * the authorization, and every one of those is on The work.
   */
  if (role === "technician") return "work";
  if (status === "estimate" || status === "scheduled" || status === "in_progress") return "work";
  if (status === "completed" || status === "invoiced" || status === "paid") return "money";
  return "details";
}

/**
 * The tab somebody chose, for as long as they are on this job. Kept per
 * job id: moving to a different job should open where that job is, not
 * where the last one was left.
 */
const chosenTab = new Map<string, TabKey>();

function wireTabs(jobId: string): void {
  const show = (key: TabKey) => {
    chosenTab.set(jobId, key);
    document.querySelectorAll<HTMLElement>(".jpanel").forEach((p) => {
      p.hidden = p.dataset["panel"] !== key;
    });
    document.querySelectorAll<HTMLElement>(".jtab").forEach((t) => {
      t.classList.toggle("on", t.dataset["tab"] === key);
    });
  };
  document.querySelectorAll<HTMLButtonElement>(".jtab").forEach((t) =>
    t.addEventListener("click", () => show((t.dataset["tab"] ?? "work") as TabKey)));

  /**
   * ANYTHING ELSE ON THE PAGE THAT POINTS AT A PANEL.
   *
   * "See the signature" sits in the always-visible band at the top and the
   * signature itself lives in History. A plain anchor would scroll to an
   * element inside a hidden panel, which is to say to nothing at all — so
   * the tab is opened first and the thing is then scrolled to.
   */
  document.querySelectorAll<HTMLElement>("[data-goto]").forEach((b) =>
    b.addEventListener("click", () => {
      const key = (b.dataset["goto"] ?? "work") as TabKey;
      show(key);
      // Scroll to the thing that was asked for, when one was named. This
      // used to scroll to the authorizations block whatever the caller
      // wanted, which was right for its one caller and wrong the moment
      // there were two.
      const target = b.dataset["scrollTo"] ?? (key === "history" ? "authorizations" : "");
      if (target) {
        document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }));

  show(chosenTab.get(jobId) ?? "work");
}

export async function jobPage(id: string, role: Role = "viewer"): Promise<void> {
  /**
   * KEEP THE PAGE WHERE IT WAS.
   *
   * Adding a line rebuilds this card, which threw the reader back to the
   * top every time — on a long quote that is indistinguishable from the
   * screen breaking, and it is half of what "the quote is glitching out"
   * meant. Redrawing is still the right thing when the row set changes;
   * losing your place is not part of it.
   */
  const wasAt = window.scrollY;
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);
  /* Whether this card says car or door. Cached for the session, so this
     costs a round trip once and nothing after. See src/domain/trade.ts. */
  const w = words(await shopTrade());
  let job, lines, auths, events;
  try {
    job = await getJob(id);
    if (!job) { mount(`<div class="page"><div class="empty">That job is not here.</div></div>`); return; }
    [lines, auths, events, rangLine, reviewShop] = await Promise.all([
      listLines(id), listAuthorizations(id), listEvents(id),
      // Which market this job belongs to, for the review link. A miss is
      // not a failure — the area code answers next.
      trackingLineFor(job.customers?.phone ?? null).catch(() => null),
      shopReviewLink(),
    ]);
  } catch (e) {
    mount(`<div class="page"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`);
    return;
  }

  const total = job.totals.total;
  const live = auths.filter((a) => !a.declined);
  /**
   * A FREE ESTIMATE VISIT. Anthony, 2026-09-29: a garage door shop's quote
   * is "the customer saying yes to a free visit for an estimate", one line
   * at nothing (src/domain/visitquote.ts). The card has to treat that as a
   * quote worth sending and a yes worth booking, where every other job with
   * nothing priced is a quote somebody has not finished.
   *
   * And its $0 yes covers the visit and nothing more: once the technician
   * prices the door, yesCovers() asks for a yes with money on it.
   */
  const visitQuote = w.quoteIsVisit && isVisitOnly(lines);
  const yesOnFile = yesCovers(live, lines.some((l) => Number(l.unit_price) > 0));
  const signed = live.filter((a) => (a.signature ?? "").trim());
  /* The teardown yes, if there is one. Its own kind since 0127, so it is
     pulled out of `live` rather than counted among the repair yeses --
     agreeing to have the engine opened is not agreeing to the repair.
     Once it exists the form is gone: the disclosure was made at a moment
     that has passed and a second one would be a different agreement. */
  /* THE GAUGES. The card appears only on a job with air conditioning
     work on it, and only once the columns exist -- `ac_high_psi` is
     undefined rather than null until 0126 lands, and a screen whose
     column is not there yet hides itself rather than showing a
     technician a database error. */
  const acKnown = job.ac_high_psi !== undefined;
  const acNow = readingsOf(job as unknown as Record<string, unknown>);
  const acWanted = acKnown && (acLines(lines ?? []).length > 0 || anyReading(acNow));
  const acWarn = acWanted ? acNag(lines ?? [], acNow) : null;
  const acField = (column: string, value: number | null | undefined) => editable({
    table: "jobs", id: job.id, column,
    // String(0) is "0" and stays visible. `?? ""` and not `|| ""`,
    // because `0 || ""` is the empty string and that is the bug.
    value: value === null || value === undefined ? null : String(value),
    placeholder: "Not taken",
    editable: job.status !== "cancelled",
    reason: "This job is cancelled.",
    save: (next) => setAcReading(job.id, column, next),
  });

  const teardownYes = agreedTeardown(auths);
  /* Same gate the repair form uses: anybody who can open the job can
     record what a customer said, and a cancelled job is finished. A
     viewer cannot save one regardless -- row level security refuses the
     insert, which is where that rule belongs. */
  const mayRecordHere = job.status !== "cancelled";

  // The bill, if there is one — the last two stops on the track cannot be
  // worked out from the job's own status alone.
  const sb = await db();
  const [onJob, crew, desk] = await Promise.all([
    jobTechnicians(id), technicians(), advisors().catch(() => [])]);

  /**
   * WHO IS ON THIS JOB, at the top, in the board's own colors. See
   * src/ui/whobar.ts. Built here rather than inside a tab because the two
   * questions it answers — who is doing it, who is looking after the
   * customer — are the two asked most often about any job.
   */
  const onIt = onJob
    .map((t) => crew.find((c) => c.id === t.technician_id))
    .filter((c): c is NonNullable<typeof c> => Boolean(c))
    .map((c) => ({ id: c.id, name: c.name }));
  const theAdvisor = desk.find((d) => d.id === job.advisor_id) ?? null;
  /* Whether the person reading this is already on the job. Null means they
     are not a technician record at all, which is the owner-operator case. */
  const theShopAddress = await shopAddress().catch(() => null);
  const myTechId = await myTechnicianId().catch(() => null);
  const meOnJob = Boolean(myTechId && onJob.some((t) => t.technician_id === myTechId));
  // Putting somebody on a job is a scheduling decision, not an edit to a
  // field, so it follows the same roles that may move a job along.
  const mayAssign = role === "admin" || role === "supervisor" || role === "advisor";
  const { data: invs } = await sb.from("invoices").select("id,status").eq("job_id", id).limit(5);
  const invoices = (invs ?? []) as Array<{ id: string; status: string }>;
  const money$ = await moneyFor(id, total);
  /**
   * A PRE-PURCHASE INSPECTION, AND WHO IS WORKING IT.
   *
   * Decides three things on this card: the deposit card is offered as a
   * Prepayment whatever the parts come to, the next step after the yes is
   * taking it, and the advisor line says which desk claimed it. See
   * src/domain/ppi.ts and src/data/ppi.ts.
   */
  const ppiJob = isPpiJob({ title: job.title, complaint: job.complaint, lines });
  const ppiClaim = ppiJob ? await claimOf(id, job.advisor_id ?? null).catch(() => null) : null;
  /* A CASE FILE IS PAID UP FRONT. Anthony, 2026-10-14: "Since they pay up
     front, that step needs moved right after the agreement is signed." The
     card is offered on every priced case (NYIA's $1,400 of hours and a $100
     vehicle fee fell through offersPrepay: not labor-only, parts not over
     $100) and drawn under the agreement, above the tabs. */
  const upFront = Boolean(w.caseFile);
  const depWord = upFront ? "Payment up front" : prepayLabel(ppiJob, w.partsLabel);
  /**
   * FINISHED, PRICED, AND NOT BILLED.
   *
   * The one state in which the customer's page shows a total and offers
   * no way to settle it, because the card form only ever mounts on an
   * open invoice. Worth a name, because two different cards ask about it.
   */
  /* Only worth asking while this job could still raise a bill; once it
     has one the question has been answered, rightly or wrongly. */
  const alsoBilled = money$.invoiceId ? null : await billedElsewhere(id);
  const finishedUnbilled = job.status === "completed" && !money$.invoiceId
    && total > 0.005 && !job.no_charge_reason;
  // Card is Supercanic-only (see shopTakesCard); every shop can take the rest.
  const canCard = await shopTakesCard();
  const payMethods = (canCard
    ? ["cash", "card", "check", "transfer"]
    : ["cash", "check", "transfer"]) as Method[];

  const facts = {
    status: job.status,
    hasPricedLine: lines.some((l) => Number(l.unit_price) > 0),
    hasAuthorization: yesOnFile,
    visitQuote,
    scheduledAt: job.scheduled_at,
    hasInvoice: invoices.length > 0,
    carIdentified: Boolean(job.vehicles
      && (String(job.vehicles.license_plate ?? "").trim() || String(job.vehicles.vin ?? "").trim())),
    invoicePaid: invoices.some((i) => i.status === "paid"),
    /* A pre-purchase inspection is paid before it is done, so its next
       step after the yes is the prepayment, not "wait for Done". */
    ppi: ppiJob,
    prepaid: Boolean(money$.deposit && money$.deposit.paid >= money$.deposit.total - 0.005),
  };
  const step = nextStep(facts);
  const stars = milestones(facts);
  const lit = starsEarned(stars);

  // THE FIVE STARS. The headline, readable from across a room, and the
  // reason a star is earned is on it rather than needing to be known.
  /* GREEN CHECKS, THE SAME ONES THE CUSTOMER SEES.
     Anthony, 2026-09-24: "we want green checkmarks like the customers get
     not the stars." The customer's page already drew this as a row of
     rings joined by a line, a tick in each one that is done. The desk now
     reads the same way, so the shop and the customer are looking at the
     same picture of the same job. A tick or an empty ring, not a colour
     alone, so it survives greyscale and a colorblind reader. */
  const starRow = `
    <ol class="checks" aria-label="${esc(lit)} of ${stars.length} done">
      ${stars.map((m) => `
        <li class="chk${m.earned ? " on" : ""}" title="${esc(m.what)}">
          <span class="chk-dot" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>
          <span class="chk-lab">${esc(m.label)}<span class="vh">${m.earned ? ", done" : ", not yet"}</span></span>
        </li>`).join("")}
    </ol>`;

  // THE TRACK. Eight stops from the call to cleared, so anybody glancing
  // at the job knows where it stands without reading a word of it.
  const jf = (column: string, value: string | null, placeholder: string, multiline = false) => {
    const key = `job.${column}` as Field;
    return editable({
      table: "jobs", id, column, value, placeholder, multiline,
      editable: canEdit(role, key), reason: whyNot(role, key),
      save: (next) => updateJobField(id, column, next),
    });
  };

  /**
   * NOTES, WITHOUT THE MACHINERY.
   *
   * The technician's path stores its tests as a fenced JSON block inside
   * this same column. An advisor must never see it and must never be able
   * to delete it by editing the sentence above it, so the box is given the
   * prose and the block is spliced back on the way out.
   */
  const jfNotes = (value: string | null) => {
    const keep = fencedTests(value);
    return editable({
      table: "jobs", id, column: "notes", value: humanNotes(value),
      placeholder: "Add a note", multiline: true,
      editable: canEdit(role, "job.notes"), reason: whyNot(role, "job.notes"),
      save: (next) => updateJobField(id, "notes",
        keep ? `${next.trim()}${next.trim() ? "\n\n" : ""}${keep}` : next),
    });
  };

  /**
   * Plate and VIN, on the job rather than buried on the vehicle record.
   * Either one closes the job; both are better. A technician standing at
   * the car is the person who can actually read them.
   */
  /**
   * A field on the CUSTOMER, edited from the job they are on. Same shape
   * as vf() below, and for the same reason: the person who has the fact
   * is looking at the job, not at the customer record.
   */
  const cf = (column: string, value: string | null, placeholder: string) => {
    const cid = job!.customers?.id;
    if (!cid) return `<span class="quiet">no customer on this job</span>`;
    return editable({
      table: "customers", id: cid, column, value, placeholder,
      editable: role !== "viewer",
      reason: "Read-only logins cannot change customer details",
      /* THE ADDRESS IS THE ONE FIELD WITH A LOOKUP, and it is the one
         field whose shape anything else depends on. 134 of the 182 jobs
         that could not be mapped were addresses with no town in them —
         "41963 Briarwood Avenue" — which no parser recovers. Picking from
         the list writes "street, town, ST zip", the shape the map already
         reads, so the pin exists because of how it was typed rather than
         in spite of it. Every other field is a plain box. */
      ...(column === "address" ? { suggest: suggestAddress } : {}),
      save: async (next) => { await updateCustomerField(cid, column, next); },
    });
  };

  const vf = (column: string, value: string | null, placeholder: string) => {
    const vid = job!.vehicles?.id;
    if (!vid) return `<span class="quiet">no ${esc(w.thing)} on this job</span>`;
    return editable({
      table: "vehicles", id: vid, column, value, placeholder,
      // Not a protocol field, so the ordinary field rules apply — and a
      // technician is exactly who should be filling these in.
      editable: role !== "viewer",
      reason: "Your login can look but not change.",
      save: (next) => updateVehicleField(vid, column, next),
    });
  };

  const track = `
    <div class="track">${TRACK.map((label, i) => `
      <div class="stop ${i < step.at ? "past" : i === step.at ? "now" : ""}${step.halted ? " halted" : ""}">
        <span class="dotm">${i < step.at ? "&#10003;" : ""}</span>
        <span class="stop-l">${esc(label)}</span>
      </div>`).join("")}</div>`;

  /**
   * THE BUTTONS THAT MOVE IT ALONG.
   *
   * The card already said what to do next; now it does it. The primary is
   * whatever this job wants — start it, finish it, bill it — and beside it,
   * only while it is booked and not started, is the one message a
   * technician sends more than any other.
   *
   * "On my way" is deliberately NOT a stage. It changes nothing about the
   * job and it is the message the customer most wants and least often
   * gets, so it can never be the wrong button to press. Marking a job
   * started is a stage and goes through the same protocol gate as every
   * other one, override and all — a technician who has not got an
   * authorization on file still gets stopped, which is the whole point of
   * the gate.
   */
  const canMove = role !== "viewer";

  /**
   * WHETHER THE STEP-BY-STEP PATH IS THE RIGHT WAY INTO THIS JOB.
   *
   * This used to require the job to be BOOKED, on the reasoning that "a
   * quote nobody has agreed to is not a job to walk through". True in a
   * shop with a desk: somebody prices it, somebody books it, and only then
   * does a technician stand at the car.
   *
   * A solo owner-operator never books anything. Matt Bolen wrote two jobs,
   * priced one at $1,455.73, took ten photographs of the car and finished
   * the work -- and both are still sitting at `estimate`, with no yes on
   * file, no invoice and no payment, because the only guided path into a
   * job appeared at a stage his jobs never reach.
   *
   * So the walk starts at the quote. It does NOT skip the rule: step one
   * still refuses to start work with no authorization on the job. The
   * difference is that it now says so in the place where the person is
   * standing, and offers to record the yes there, rather than being an
   * invisible button on a screen they never got to.
   */
  const walkable = techPathOn(role) && canMove
    && ["estimate", "scheduled", "in_progress"].includes(job.status);

  /**
   * TWO DOORS, AND ONLY WHEN THERE IS SOMETHING BEHIND THEM.
   *
   * Anthony: "Anyone with an admin role, supervisor, Owner role should also
   * be able to see both tracks as long as there are things remaining to be
   * done in both. Clicking the entry point for one wizard track or the
   * other will take you into the wizard for one or the other."
   *
   * The rules for what is left live in domain/tracks.ts and are read here
   * from records this page already loaded, so the count on the card and the
   * step the walk opens on cannot disagree. A track with nothing left draws
   * no card: a door into an empty room is how people learn to stop opening
   * doors.
   */
  const trackFacts = factsFrom(job, lines, {
    live: live.length,
    crew: onJob.length,
    invoiced: Boolean(money$.invoiceId),
    paidAmount: Number(money$.paid ?? 0),
    found: hasDiagnosis(job),
  });
  const myTracks: TrackKey[] = canMove ? tracksFor(role) : [];
  const doors = myTracks
    .map((t) => ({ track: t, ...summaryOf(t, trackFacts, currentTrade()) }))
    .filter((d) => d.left > 0);
  /* Whichever half is actually being worked leads. Before anybody is at the
     car the desk leads; once the car is up on the lift it is the other way
     round, and the person holding both hats should not have to read two
     cards to find the one they are living in. */
  if (job.status === "in_progress" || job.status === "completed") doors.reverse();
  const goldFrame = holdsBoth(role) && doors.length === 2;
  /* Whether "On my way" is on the strip. Read twice below: once to draw
     the button, once to say so in a class the stylesheet can match on. */
  const omwHere = walkable && step.alsoTells && Boolean(job.customers?.phone);

  const DOOR: Record<TrackKey, { kind: string; label: string; under: string; href: string }> = {
    advisor: { kind: "Service advisor", label: "The desk side",
      under: "Price it, send it, get the yes, book it, bill it",
      href: `#/advisor/${job.id}` },
    tech: { kind: "Technician", label: `At the ${w.thing}`,
      under: "Check in, inspect, diagnose, repair, close",
      href: `#/tech/${job.id}` },
  };
  const openTab: TabKey = chosenTab.get(id) ?? defaultTab(job.status, role);

  /**
   * Their other work, so "have we had this in before" does not mean
   * opening the customer record in a second tab. Asked for by customer
   * rather than filtered out of the newest few, which would have shown a
   * regular's history and a stranger's nothing for the same reason.
   */
  type Other = {
    id: string; job_number: string; status: Status; title: string | null;
    complaint: string | null; created_at: string;
    line_items: Array<{ total: number | null }> | null;
  };
  const theirs: Array<Other & { total: number }> = [];
  if (job.customers) {
    const { data: rows } = await sb
      .from("jobs")
      .select("id,job_number,status,title,complaint,created_at,line_items(total)")
      .eq("customer_id", job.customers.id)
      .neq("id", id)
      .order("created_at", { ascending: false })
      .limit(12);
    for (const o of (rows ?? []) as unknown as Other[]) {
      theirs.push({
        ...o,
        total: (o.line_items ?? []).reduce((n, l) => n + Number(l.total ?? 0), 0),
      });
    }
  }
  /**
   * What stands between this job and its next stage. Computed from the
   * same facts the gate uses, so the card and the refusal can never
   * disagree about why.
   */
  const stuck = step.move && (step.move.kind === "status" || step.move.kind === "invoice")
    ? whatBlocks(facts, step.move.kind === "invoice" ? "invoiced" : step.move.to)
    : [];
  /* WHO HAS THIS INSPECTION, said plainly, and a warning -- not a wall --
     when it is somebody other than the person reading. Two desks work
     PPIs; this line is how they stop texting the same buyer twice. */
  const ppiWho = ppiJob ? `
    <div class="fine"${ppiClaim && !ppiClaim.mine ? ` style="color:var(--warn,#b45309)"` : ""}>
      ${esc(advisorLine(ppiClaim?.name ?? null, ppiClaim?.via ?? null))}${ppiClaim && !ppiClaim.mine
        ? ` &middot; somebody else is working this one. Anything you do here still goes through; let them know.` : ""}
    </div>` : "";
  const nextBlock = `${ppiWho}
    <div class="next ${step.done ? "done" : step.halted ? "halted" : ""}">
      <div class="next-h">${step.done ? "Finished" : step.halted ? "Stopped" : "What is next"}</div>
      <div class="next-a">${esc(step.action)}</div>
      <div class="next-w">${esc(step.because)}</div>
      <!-- WHAT IS IN THE WAY, said before anybody presses anything. The
           same rules the gate enforces, in advance rather than after the
           fact — a technician at a car should be able to read what is
           missing without pressing a button to be told no. -->
      ${stuck.length ? `
        <div class="stuck">
          <div class="stuck-h">${stuck.length === 1
            ? "One thing is in the way" : `${stuck.length} things are in the way`}</div>
          <ul>${stuck.map((w) => `<li>${esc(w)}</li>`).join("")}</ul>
        </div>` : ""}
      ${canMove && (step.move || step.alsoTells) ? `
        <div class="next-do${walkable ? " next-walk" : ""}">
          <!-- ONE WAY IN, NOT TWO.
               Anthony: "so far down the page I have these options, none say
               wizard or steps, they look like two different ways to start
               job." They were. This link and the move button below were both
               btn primary, sat side by side, and said "Start job" and "Start
               the job" — which is not a choice, it is a coin toss.
               So where the path is available it REPLACES the plain move
               button rather than joining it, and it says what it actually
               does. The plain button comes back the moment the path is not
               on, so production is untouched. -->
          <!-- The walk and On my way live in the strip above the tabs
               now. Rendering them here too would put two elements with the
               same id on the page, and the handler would wire the wrong
               one. -->
          ${step.move && !walkable ? `<button class="btn primary" id="movebtn">${
            esc(step.move.label)}</button>` : ""}
          ${step.alsoTells && job.customers?.phone && !walkable
            ? `<button class="btn" id="omw">On my way</button>` : ""}
          <!-- Only while it has not gone. Once a price is out, the thing
               to do about it is not "send it again" -- it is wait, or
               chase, and the button to re-send lives on The work beside
               the lines it would send. -->
          <!-- Phone OR email. This used to require a number, so a customer
               who had given us an address and no mobile had no send button
               anywhere on the card and the quote simply could not go.
               Text is still what it presses when there is a number; email
               is what is left when there is not. -->
          ${job.status === "estimate" && (total > 0 || (visitQuote && job.customers?.phone))
            && (job.customers?.phone || job.customers?.email)
            && !job.quote_expires_at
            ? `<button class="btn" id="send-quote">${
                job.customers?.phone ? "Send it to them" : "Email it to them"}</button>` : ""}
          <!-- An inspection belongs on the PPI desk (2026-10-14). Only
               before work starts; drawn after the Supercanic check below. -->
          ${ppiJob && ["estimate", "scheduled"].includes(job.status) ? `<span id="ppi-slot"></span>` : ""}
        </div>
        <div class="msg-slot" id="move-msg"></div>` : ""}
    </div>`;

  // Signed, spoken, or nothing — three different sentences, because they
  // are three genuinely different situations and only one is a problem.
  /**
   * The estimator can only work from a car and a complaint. The line
   * editor already gates its own button on exactly this, so the band asks
   * the same question rather than inventing a second, differently wrong
   * one — offering a draft that then refuses is worse than not offering.
   */
  const canDraft = Boolean(
    vehicleName(job.vehicles).trim()
    && ((job.complaint ?? "").trim() || (job.notes ?? "").trim()));

  const needsAuth = !yesOnFile && job.status !== "cancelled";
  // Open by default where there is room for it; folded where there is not.
  const roomy = window.matchMedia("(min-width: 820px)").matches;
  /**
   * WHAT A SHUT CARD STILL ANSWERS.
   *
   * Anthony, 2026-09-20: "underneath the 6 tile section ... is a mess.
   * Leave anything not directly the next thing needed to be done
   * collapsed and let people fold it out when they need it."
   *
   * Folding only pays if the closed head answers the question people
   * opened the card to ask. "The work" says nothing and gets tapped
   * every time; "The work · Nina Reyes" is the answer, and the card
   * stays shut. It is the trick the history cards already play with
   * their counts. See src/ui/fold.ts for the fold itself -- and note
   * that the choice is remembered per person per card, so somebody who
   * wants the appointment open on every job opens it once.
   */
  const crewHead = onJob.length
    ? onJob.map((t) => crew.find((c) => c.id === t.technician_id)?.name ?? "Unknown").join(", ")
    : "nobody on it yet";
  const carHead = job.vehicles ? vehicleName(job.vehicles) : `no ${w.thing} yet`;
  const apptHead = job.scheduled_at ? shopStamp(job.scheduled_at) : "not booked";
  const moneyHead = job.no_charge_reason
    ? "not being billed"
    : money$.balance > 0.005
      ? `${money(money$.balance)} owed`
      : money$.invoiceId
        ? "settled"
        : total > 0
          ? `${money(total)}, nothing billed yet`
          : "nothing priced";

  const authLine =
    !live.length
      ? `<div class="auth none">No authorization on this job yet. Nothing should be worked on.</div>`
      : signed.length
        ? `<div class="auth ok">Signed by ${esc(signed[0]!.authorized_by)} for ${money(signed[0]!.amount)}${
             signed[0]!.method === "portal" ? " on their own screen" : ""}
             <span class="when">${esc(since(signed[0]!.authorized_at))}</span>
             <button class="auth-see" data-goto="history">See the signature</button></div>`
        : `<div class="auth ok">Authorized ${esc(live[0]!.method === "phone" ? "over the phone" : "in person")}
             by ${esc(live[0]!.authorized_by)} for ${money(live[0]!.amount)}
             <span class="when">${esc(since(live[0]!.authorized_at))}</span>
             <div class="fine">No signature, and none is required — who agreed and when is the record.</div></div>`;

  mount(`
    <div class="page">
      <header class="page-head">
        <a class="back" href="#/jobs">&larr; Jobs</a>
        <h1>${esc(job.job_number)}
          <span class="pill s-${esc(job.status)}">${esc(stateLabel(job.status, job.archived_at))}</span>
          ${w.caseFile ? "" : `<span class="wherebadge ${job.service_at ?? "unset"}">${
            icon(job.service_at === "shop" ? "shop" : "mobile")}${esc(whereLabel(job.service_at))}</span>`}</h1>
        <!-- A job with no title read as a blank line where the work
             should be. It falls back to the customer's own words now,
             trimmed to a heading, and stays editable. -->
        <p>${jf("title", job.title || suggestTitle(job), "What this job is")}</p>
      </header>

      <!-- NOT ON A CASE FILE (trade.ts caseFile). Neelum circled this bar,
           "No technician / Advisor: Nobody yet", as the car language she
           wanted gone, 2026-10-14. -->
      ${w.caseFile ? "" : whoBarHtml(onIt, theAdvisor, mayAssign, desk)}

      <!-- ─── WHAT THIS JOB IS ────────────────────────────────────────
           The answer before the drawers. The heading carries the
           customer's own words and the tabs carry the detail; this is the
           shop's one-line answer to them, and it is what somebody opening
           a job on a phone is actually looking for.

           It is quiet on purpose. It states a fact and offers no button,
           because every button on this screen already lives somewhere a
           person has learned to look for it. -->
      ${(() => {
        const sum = repairSummary(job, lines!, w.partLabel, w.partsLabel);
        /* "No case" sat first on NYIA's summary line: the empty-vehicle
           phrase, for a record a case file never fills in. */
        const car = w.caseFile && !job.vehicles ? "" : vehicleName(job.vehicles);
        return `
        <div class="jsum${sum.priced ? "" : " unpriced"}">
          <div class="jsum-what">${sum.what
            ? esc(sum.what)
            : `<span class="quiet">Nothing priced on this job yet</span>`}</div>
          <div class="jsum-meta">
            ${car ? `<span class="jsum-car">${esc(car)}</span>` : ""}
            ${sum.bits.map((b) => `<span>${esc(b)}</span>`).join("")}
            ${total > 0 ? `<span class="jsum-money">${money(total)}</span>` : ""}
          </div>
        </div>`;
      })()}

      <!-- THE MECHANIC'S TWO BUTTONS, AT THE TOP, IN ORDER.
           Anthony: "have it nearer to the top of the card for the mechanic
           when they click 'on the way' then they get 'start the job' and go
           to the wizard."

           They were at the bottom. The card carrying them renders AFTER
           the whole tab panel, so somebody standing at a car had to scroll
           past six drawers of the desk's information to find the one thing
           they came to press. That is the "so far down the page" from
           earlier, and moving the buttons is the fix rather than making
           them louder where they were.

           TWO STEPS, NOT TWO CHOICES. Telling the customer you are coming
           and starting the job are not alternatives, they are Tuesday
           morning in order. So a job that is booked shows On my way as the
           action and the walk as the faint next thing; pressing it swaps
           them, and a job already under way only ever shows the walk.

           WHETHER THAT BUTTON IS THERE IS SAID ON THE STRIP ITSELF, as
           the hasomw class. The dimming rule used to ask the DOM with
           :has(.mech-omw), which is banned here — the build targets
           safari15, where an unsupported selector is not an error but
           silence, and this particular silence leaves the walk card at
           full strength beside the louder button it is meant to sit
           behind. The class is computed once, where the condition
           already lives. -->
      ${doors.length && !w.caseFile ? `
        <div class="mechstrip ${job.status === "in_progress" ? "running" : "heading"}${
             goldFrame ? " both" : ""}${omwHere ? " hasomw" : ""}" id="mechstrip">
          ${goldFrame ? `<div class="mechstrip-k">Both sides of this job are yours</div>` : ""}
          ${omwHere
            ? `<button class="btn primary mech-omw" id="omw">On my way</button>` : ""}
          ${doors.map((d) => {
            const face = DOOR[d.track];
            /* What it SAYS is the next real thing, not the track's name.
               "Send it to them" tells somebody whether to press it; "Open
               the advisor wizard" makes them press it to find out. */
            const head = d.next ? d.next.title : face.label;
            const sub = d.next ? d.next.sub : (d.blockedBy ?? face.under);
            return `
            <a class="walkcard ${esc(d.track)}" id="walk-${esc(d.track)}" href="${esc(face.href)}">
              <span class="walkcard-k">${esc(face.kind)} &middot; ${d.left} left</span>
              <span class="walkcard-t">${esc(head)}</span>
              <span class="walkcard-s">${esc(sub)}</span>
              <span class="walkcard-go" aria-hidden="true">&rarr;</span>
            </a>`;
          }).join("")}
        </div>` : ""}

      <!-- LEFT TO RIGHT, THE WAY THE OLD ONE DID IT.
           Anthony: "We like the left to right tabs that changed from like
           communication, notes, details, etc we had in the old one."

           What stays ABOVE the tabs is the band nobody should have to go
           looking for: where the job is, what to do next, what is in the
           way, and whether anybody has said yes. Everything else is a
           drawer.

           THE TABS DO NOT MOVE, but which one opens does. A quote opens on
           the work, because it needs a price. A job under way opens on the
           work too. A finished one opens on the money. That is "ordered by
           step" without the furniture rearranging itself under somebody
           who has learned where things are. -->
      <!-- THE AGREEMENT FOR SIGNATURE, ABOVE THE TABS (esign-card.ts).
           It used to sit third card down on History, the last tab. On
           2026-10-14 Neelum and Laura at NYIA looked all over the desk for
           it, then watched the how-to video and still could not find it.
           Sending the retainer is the next step on a case, so it belongs in
           the band nobody should go looking for. It stays hidden for a shop
           with signing switched off. -->
      <div class="card" id="esign-card" hidden></div>

      <!-- THE LOSS FILE, for a restoration company only
           (src/pages/restoration.ts, 2026-10-28). Hides itself until it
           has read jobs.loss, and stays hidden if that column is not there. -->
      ${w.lossFile ? `<div class="card" id="lossfile"></div>` : ""}

      <div class="jtabs" id="jtabs">
        ${TABS.map((t) => `<button class="jtab ${t.key === openTab ? "on" : ""}"
          data-tab="${t.key}">${esc(t.key === "vehicle" ? cap(w.thing) : t.label)}${
            t.key === "work" && lines!.length ? `<i>${lines!.length}</i>` : ""}${
            t.key === "money" && money$.balance > 0.005 ? `<i class="owed">owed</i>` : ""}
          </button>`).join("")}
      </div>

      <!-- PHOTOS, UNDER THE SIX TABS AND ABOVE EVERY PANEL. Anthony wanted the
           camera reachable from any tab and found before you open one, so it
           lives here rather than inside Work. Folded shut on load — the header
           row (a count and the one-tap camera) is what you see; the gallery is
           a tap away. See jobPhotos(..., { shut: true }) below. -->
      <div class="card" id="jobphotos"></div>

      <div class="jpanel" data-panel="details">
      <!-- ─── THREE DRAWERS, ONE OPEN ─────────────────────────────────
           Anthony, 2026-09-20: "underneath the 6 tile section of The
           work, History etc is a mess. Leave anything not directly the
           next thing needed to be done collapsed and let people fold it
           out when they need it."

           The three groups below were already three groups -- headings
           inside one tall card. That made them readable and did nothing
           at all about the scroll, because a heading you have to scroll
           past is still a thing you scroll past. They are three cards
           now and only the first is open: whose job this is. The car,
           the crew, the complaint and the notes are one tap away, and
           each shut head carries its own answer, so most of the taps
           never happen. -->
      <div class="grid2 jdetgrid">
        <div class="gcol">
        <div class="card" data-fold="open">
          <div class="card-head">Who${job.customers ? ` &middot; ${esc(job.customers.full_name)}` : ""}</div>
          <div class="pad">
          <div class="kv"><span>Customer</span><b>${job.customers
            ? `<a href="#/customer/${job.customers.id}">${esc(job.customers.full_name)}</a>`
            : "—"}</b></div>
          <div class="kv"><span>Phone</span><b>${job.customers?.phone
            ? `<a href="tel:${esc(job.customers.phone)}">${esc(job.customers.phone)}</a>` : "—"}</b></div>
          <!-- THE EMAIL, HERE, AND EDITABLE.

               It was on the customer's own record and nowhere else, so an
               advisor standing on a job had no field to put it in.
               Anthony typed Jonathan Roman's address into the job NOTES,
               which is exactly what a person does when the box they need
               is one screen away. An invoice cannot be sent without it. -->
          <div class="kv"><span>Email</span><b>${job.customers
            ? cf("email", job.customers.email ?? null, "Add an email")
            : `<span class="quiet">no customer on this job</span>`}</b></div>
          <!-- THEIR ADDRESS, WITH THEIR PHONE AND EMAIL. Anthony (D12): "On
               mobile, move customer address into the contact block with
               phone and email in the customer card so it's easy to find."
               This is where they live; "Where the van is going" below is
               this job's location, which is usually but not always the
               same place. -->
          ${job.customers ? `<div class="kv"><span>${w.caseFile ? "City" : "Address"}</span><b>${
            cf("address", job.customers.address ?? null, w.caseFile ? "Add a city, or an address" : "Add their address")}
            ${job.customers.address ? `<a class="navlink" target="_blank" rel="noopener"
              href="https://www.google.com/maps/dir/?api=1&destination=${
                encodeURIComponent(job.customers.address)}">navigate</a>` : ""}</b></div>` : ""}
          </div>
        </div>
        <!-- A JOB WITH NO CAR HAD NO WAY TO GET ONE.
             Plate and VIN below are edits to the vehicle RECORD, so with
             no vehicle attached there was nothing for them to write to
             and no button anywhere that made one. An advisor drafting a
             quote off a phone call hit that every time. -->
        <!-- THE CAR IS EDITED ON ITS OWN TAB, and only there. This is the
             name and a way to reach it. -->
        <div class="card" data-fold>
          <div class="card-head">The ${esc(w.thing)}, and where &middot; ${esc(carHead)}</div>
          <div class="pad">
          <div class="kv"><span>${esc(cap(w.thing))}</span><b>${job.vehicles
            ? `${esc(vehicleName(job.vehicles))}
               <button class="linkbtn" data-goto="vehicle">edit</button>`
            : `<button class="linkbtn" id="add-car">Add the ${esc(w.thing)}</button>`}</b></div>
          <div id="add-car-host"></div>
          <!-- A CASE FILE IS ALWAYS MOBILE (Anthony, 2026-10-14: "All work is
               mobile"), so there is nothing to pick. -->
          ${w.caseFile ? "" : `<div class="kv"><span>Where</span><b>
            <span class="choice" id="where-pick">
              ${(["mobile", "shop"] as const).map((k) => `
                <button class="chip ${job.service_at === k ? "on" : ""}" data-at="${k}">${
                  icon(k)}${esc(whereLabel(k))}</button>`).join("")}
            </span></b></div>`}
          <!-- ─── ONE ADDRESS, NOT TWO ────────────────────────────────
               Anthony: "there is the main address field that works and
               another under the where mobile/shop toggle ... Can we not
               have the second one there."

               There were two editable addresses on one card: the
               customer's, in the contact block above, and the job's, here.
               Both had a navigate link, both looked authoritative, and
               nothing on screen said which one the van was going to. On an
               in-shop job the answer is neither: it is the shop's own
               address, which the shop has already written down once in its
               profile.

               So this is a READ-OUT, not a field. It resolves to the right
               place and links to it, and the one place to change anything
               is the customer's address above or the shop's own profile.
               The jobs.location column is still written by intake and
               still read by the referral sweep; it simply is not typed
               here any more. -->
          ${(() => {
            // One rule, shared with the invoice, which has to print this
            // same answer to satisfy the mobile invoice requirement.
            const where = whereItWasDone({
              service_at: job.service_at,
              location: job.location,
              customerAddress: job.customers?.address ?? null,
              shopAddress: theShopAddress,
            })?.address ?? null;
            if (!where) {
              if (w.caseFile) return "";
              return `<div class="kv"><span>Address</span><b><span class="quiet">${
                job.service_at === "shop"
                  ? `No address on the shop's profile yet. Add it once in Settings and every in-shop job uses it.`
                  : `No address on this customer yet. Add it under Contact above.`
              }</span></b></div>`;
            }
            return `<div class="kv"><span>Address</span><b>${esc(where)}
              <a class="navlink" target="_blank" rel="noopener"
                 href="https://www.google.com/maps/dir/?api=1&destination=${
                   encodeURIComponent(where)}">navigate</a>
              <span class="fine">${job.service_at === "shop"
                ? "the shop's own address" : "the customer's address"}</span>
              </b></div>`;
          })()}
          </div>
        </div>
        <!-- WHO IS ON IT. job_technicians has existed since the first
             migration and nothing ever wrote to it, so every job in here
             showed nobody working it. A shop with three vans cannot
             answer "who has the Tundra" from a screen that never asks. -->
        <div class="card" data-fold>
          <div class="card-head">The work &middot; ${esc(crewHead)}</div>
          <div class="pad">
          <div class="kv"><span>Who is on it</span><b>
            <span class="crew" id="crew">${
              onJob.length
                ? onJob.map((t) => {
                    const name = crew.find((c) => c.id === t.technician_id)?.name ?? "Unknown";
                    return `<span class="crew-one${t.is_lead ? " lead" : ""}">${esc(name)}${
                      t.is_lead ? `<i>lead</i>` : ""}</span>`;
                  }).join("")
                : `<span class="quiet">Nobody yet</span>`}
            </span>
            ${canEdit(role, "job.notes") ? `
              <button class="linkbtn" id="crew-edit">change</button>` : ""}
            <!-- ─── THE OWNER WHO IS ALSO THE TECHNICIAN ────────────────
                 Anthony: "Matt Bolen is both owner and operator, needs to
                 be able to be service advisor and tech."

                 Roles here were built for a shop with a desk and a yard,
                 where the technicians table is a different table from profiles
                 because they are different people. In a one-person shop
                 they are the same human, and signing up as the owner made
                 a profile and not a technician -- so there was nobody the
                 system would let him put on his own jobs, and every job he
                 has ever done says Nobody yet.

                 One press makes the record, links it to the login, and
                 puts them on the job. -->
            ${canMove && !meOnJob ? `
              <button class="linkbtn" id="crew-me">put me on it</button>` : ""}
          </b></div>
          <div id="crew-host"></div>
          <div class="kv"><span>${esc(w.complaintLabel)}</span><b>${
            jf("complaint", job.complaint, w.complaintHint, true)}</b></div>
          <!-- THE OLD FENCE MUST NOT BE RENDERED RAW INTO THIS BOX.
               The technician's path used to fence a JSON block of tests
               into notes so it did not have to wait on a migration. That
               step is gone (2026-09-21) but the fences are still sitting
               in the notes of every job from before it, and rendering one
               raw would put a wall of braces in the middle of the details
               tab -- worse, an advisor editing around it would destroy it
               on save. So the box shows the prose and the block is
               re-attached on the way out. See src/data/tests.ts. -->
          <div class="kv"><span>Notes</span><b>${
            jfNotes(job.notes)}</b></div>
          </div>
        </div>
        </div>
        <div class="card pad">
          <div class="big" id="job-total-big">${money(total)}</div>
          <div class="kv"><span>${w.caseFile ? "Service" : "Labor"}</span><b>${money(job.totals.labour_total)}</b></div>
          <div class="kv"><span>${esc(cap(w.partsLabel))}</span><b>${money(job.totals.parts_total)}</b></div>
        </div>
      </div>

      <!-- The stage chips and the appointment are a control somebody
           goes looking for, not something to read on the way past. The
           head says when the car is booked in, which is the only part
           of it anybody reads on the way past. -->
      <div class="card" data-fold>
        <div class="card-head">Stage and appointment &middot; ${esc(apptHead)}</div>
        <div class="pad">
        <div class="card-sub">Stage</div>
        <div class="stagepick" id="stagepick">
          ${STATUS.map((st) => `
            <button class="chip ${job.status === st ? "on" : ""}" data-st="${st}">${
              esc(STATUS_LABEL[st])}</button>`).join("")}
        </div>
        <div class="card-sub" style="margin-top:16px">Appointment</div>
        <!-- SAVED ON "SET", NOT ON EVERY TICK OF THE PICKER. Anthony (D11),
             job 1627: "controls not responding right, autosaving at 10am".
             A phone's date and time wheels fire change on every movement,
             and each change saved the half-chosen value and redrew the
             whole page under the open picker, so the picker fought the user
             and 1627 landed at 6:07am. The picker is left alone until a
             person says the time is right. -->
        <!-- DATE AND TIME AS TWO PLAIN INPUTS. Anthony (D13), still on 1627:
             saving 6 PM on the phone "won't save". A combined datetime-local
             input is the one control iOS Safari is least honest about: its
             value can stay empty after the wheels close. Two native pickers,
             a date and a time, commit their values every time. -->
        <div class="appt">
          <div class="appt-row">
            <input id="appt-date" type="date" value="${esc(localValue(job.scheduled_at).slice(0, 10))}">
            <input id="appt-time" type="time" value="${esc(localValue(job.scheduled_at).slice(11, 16))}">
          </div>
          <button class="btn sm" id="appt-save">Set the time</button>
          <div class="fine">${job.scheduled_at
            ? `Saved: ${esc(shopStamp(job.scheduled_at))}. `
            : "Not booked yet. "}Pick the date and the time, then Set. Clearing the date and pressing Set puts the
            job back to a quote — the board cannot hold a booking with no date.</div>
        </div>
        <div class="msg-slot" id="stage-msg"></div>
        </div>
      </div>
      </div><!-- /details -->

      <div class="jpanel" data-panel="work">
        <!-- WHO SAID YES, BESIDE WHAT THEY SAID YES TO.
             It sat in Details under the totals, which is not the screen
             anybody opens while deciding whether to start. The work and the
             permission to do it belong on one panel. -->
        <div class="card pad">${authLine}</div>
      <div class="card" data-fold="open">
        <div class="card-head">What is on it</div>
        <div id="lineedit"></div>
      </div>

      <!-- WHAT THIS QUOTE SAYS.
           The most valuable thing on the old quote and the thing the
           rebuild dropped: a paragraph in the shop's own words, under the
           prices, that the customer reads before saying yes. "We assumed
           the 5.7L V8 with four wheel drive, so both differentials are
           included; if it is two wheel drive the front service comes off
           the ticket." That is the sentence that stops an argument three
           days later.

           It is NOT the complaint. Complaint is what they told us, and
           writing our answer into it makes the next person read our
           assumptions as their words — which is exactly what the import
           did. -->
      <div class="card quotesays" data-fold${w.caseFile ? `="open"` : ""}>
        <div class="card-head">${w.caseFile ? "Additional information" : "What this quote says"}${job.quote_note ? "" : " &middot; nothing written yet"}</div>
        <div class="pad">
        <div class="card-sub">${w.caseFile ? "Optional. Prints on the quote the client reads" : "The customer reads this"}
          <i>&mdash; edit it</i></div>
        ${jf("quote_note", job.quote_note,
             w.caseFile
               ? "Anything the client should know: where, when, what is included, what to bring. Leave it empty if there is nothing."
               : "What you assumed, what you would check first, anything they should know before saying yes.",
             true)}
        ${w.caseFile ? `<p class="fine">Every quote also says: ${esc(MORE_TIME_NOTE)}</p>` : ""}
        ${job.quote_expires_at ? `<p class="fine">This price stands until ${
          esc(new Date(job.quote_expires_at).toLocaleDateString("en-US",
            { weekday: "long", month: "long", day: "numeric" }))}.</p>` : ""}
        </div>
      </div>

      <!-- ─── THE THREE THINGS YOU DO WITH A QUOTE ──────────────────
           Anthony: "We should have a draft quote, send quote and cancel
           quote options."

           They existed and they were in three different places. Drafting
           was a button inside the line editor, sending was a card further
           down, and cancelling was a status chip in the band at the top.
           So the one screen whose entire purpose is the quote never showed
           you what you could do with it, and the answer to "where do I
           send this" was "scroll".

           One band, in the order the work actually goes: draft it, send
           it, and — only once there is something to abandon — bin it. -->
      <!-- WHY THIS IS NOT A TEST FOR THE ESTIMATE STATUS ANY MORE.

           Alaina had a priced job she could not send. Nothing was broken:
           the job had been scheduled before the price ever went out, and
           scheduling moved it off estimate, which took this whole band
           off the screen. There was no send button anywhere, and nothing
           said why — so the only available reading was "it won't let me".

           Booking a car in before the customer has said yes is a habit we
           are trying to end, not a reason to withhold the one button that
           would fix it. So the band stays up until the work is finished or
           binned. Cancelling still belongs to a quote nobody has acted on,
           so that button alone keeps the narrower test. -->
      ${["estimate", "scheduled", "in_progress"].includes(job.status) ? `
        <div class="card pad quoteacts">
          <div class="quoteacts-l">
            <div class="rowlink-t">${total > 0
              ? `This quote &mdash; ${money(total)}`
              : visitQuote ? "This quote &mdash; a free estimate visit"
              : "Nothing priced yet"}</div>
            <!-- A REASON, NEVER A GAP. Every branch that hides the send
                 button says what is missing and where to fix it. The old
                 copy promised "they get a link to their own page" even
                 when there was no number to send the link to. -->
            <p class="quiet">${
              visitQuote && !job.customers?.phone
                ? "A free estimate visit goes by text, and this customer has no mobile number. Add one under Details."
                : visitQuote
                ? "No price on this one. It asks them whether they want us to come out and look, and their yes is the approval. Then give it a day."
                : total <= 0
                ? "Draft it below, or add a line with a price on it."
                : !job.customers
                ? "No customer on this job yet, so there is nobody to send it to. Add one under Details."
                : !job.customers.phone && !job.customers.email
                ? "This customer has no mobile number and no email address, so there is nowhere to send it. Add one under Details and this button comes back."
                : !job.customers.phone
                ? "This customer has no mobile number, so this one goes by email. It carries every line, the total and the same approve link a text would."
                : "They get a link to their own page, where the lines, the total and an approve button already are."}</p>
          </div>
          <div class="quoteacts-b">
            ${canDraft ? `<button class="btn" id="draft-quote-2">Draft it for me</button>` : ""}
            ${(total > 0 || visitQuote) && job.customers?.phone
              ? `<button class="btn primary" id="send-quote-2">${
                  job.quote_expires_at ? "Send it again" : "Send it to them"}</button>`
              : ""}
            <!-- THE OTHER ROAD, beside the first rather than instead of it.
                 Anthony: "Add email option for sending a quote." An advisor
                 with both should be able to pick without a menu, and an
                 advisor with only an address should not be looking at a
                 dead card. When there is no number this is the primary
                 button, because it is the only one. -->
            ${total > 0 && job.customers?.email
              ? `<button class="btn ${job.customers?.phone ? "" : "primary"}" id="email-quote">${
                  job.quote_expires_at ? "Email it again" : "Email it to them"}</button>`
              : ""}
            ${w.caseFile && total > 0
              ? `<button class="btn" id="print-quote">Print the quote</button>` : ""}
            <!-- A technician is not offered this. setStatus refuses a
                 cancel from them whatever screen asks (job 1747: a $450
                 authorized job ended by the tech it was assigned to), so
                 without this they would get a button that only ever
                 throws. The sentence in its place says who to tell. -->
            ${job.status === "estimate" && mayCancel(role).allowed
              ? `<button class="btn ghost danger" id="cancel-quote">Cancel this quote</button>`
              : job.status === "estimate"
              ? `<span class="fine">${esc(mayCancel(role).because)}</span>` : ""}
          </div>
          <div class="msg-slot" id="send-quote-msg"></div>
        </div>` : ""}

      <!-- THE PREPURCHASEAUTO REPORT on the shop's own PPI job. Anthony,
           2026-10-29: Brian Huey was on the road test at a Nissan dealer
           with his own company's PPI and the report would not open for
           him. Same 122 points as the PPI desk; finishing gives the link. -->
      ${!w.caseFile && ppiJob ? `<a class="card pad rowlink" href="#/ppi-job/${esc(id)}">
        <span class="rowlink-t">PrePurchaseAuto report</span>
        <span class="rowlink-s">The 122-point pre-purchase report, saved as you go, with a link for your customer</span>
        <span class="rowlink-go">&rarr;</span>
      </a>` : ""}

      ${w.caseFile ? "" : `<a class="card pad rowlink" href="#/inspection/${esc(id)}">
        <span class="rowlink-t">Inspection sheet</span>
        <span class="rowlink-s">Every check, saved as you go &mdash; tires optional</span>
        <span class="rowlink-go">&rarr;</span>
      </a>`}

      <!-- THE DIAGNOSTIC CARD, phase 10, beside the inspection because
           it is the next thing the technician opens at the car. The line
           under the title is the card itself, cut short, so the desk can
           read what was found without opening anything. See
           pages/diagnosis.ts. -->
      ${w.caseFile ? "" : ((found) => `<a class="card pad rowlink" href="#/diagnosis/${esc(id)}">
        <span class="rowlink-t">Diagnostic card</span>
        <span class="rowlink-s">${found
          ? esc(found.length > 140 ? `${found.slice(0, 137)}…` : found)
          : "Nothing written yet. What is wrong, in the tech&rsquo;s own words"}</span>
        <span class="rowlink-go">&rarr;</span>
      </a>`)(diagnosisOf(job))}

      <!-- ─── THE MANUAL SIDE, ON THE JOB ────────────────────────────
           Anthony, 2026-09-20: "I still don't know where you put the
           manual links on jobs." There were none. /manuals has read
           ?vin= off its own query string since it was written and
           nothing in the desk ever built that link, so the only routes
           in were the phone menu and the More page, both of which open
           the shelf empty and ask for a car you are already reading.

           HERE because this is the panel a technician lands on, beside
           the inspection sheet, which is the other thing they open
           while standing at the car. The car's own tab has the same
           link; that is deliberate, and they are one URL built by one
           function so they cannot drift. See src/domain/manuals.ts.

           Gated on the trade, not the role. Behind that door is
           recalls, wiring diagrams and factory portals, and every
           source is a vehicle manufacturer -- a locksmith offered this
           gets a shelf with nothing on it. Same gate as the other two
           doors; see the test in src/domain/trade.test.ts. -->
      ${w.hasPartsCatalog && job.vehicles ? `
        <a class="card pad rowlink" href="${esc(manualsHref(job.vehicles))}"
           target="_blank" rel="noopener">
          <span class="rowlink-t">Manuals for this ${esc(w.thing)}</span>
          <span class="rowlink-s">${esc(manualsBlurb(job.vehicles, w.thing))}</span>
          <span class="rowlink-go">&rarr;</span>
        </a>` : ""}
      </div><!-- /work -->

      <!-- ─── THE CAR ─────────────────────────────────────────────────
           Every field that says what this vehicle IS, in one place, and
           editable by anybody who is not a viewer. A technician standing
           at the car reading a door jamb is exactly who should be fixing
           a wrong VIN, and they were the ones who could not. -->
      <div class="jpanel" data-panel="vehicle">
        ${job.vehicles ? `
          <div class="card pad">
            <div class="kv"><span>${esc(w.yearLabel)}</span><b>${
              vf("year", job.vehicles.year === null ? null : String(job.vehicles.year),
                 `Add the ${w.yearLabel.toLowerCase()}`)}</b></div>
            <div class="kv"><span>${esc(w.makeLabel)}</span><b>${
              vf("make", job.vehicles.make, `Add the ${w.makeLabel.toLowerCase()}`)}</b></div>
            <div class="kv"><span>${esc(w.modelLabel)}</span><b>${
              vf("model", job.vehicles.model, `Add the ${w.modelLabel.toLowerCase()}`)}</b></div>
            <!-- COLOR. Carried on the record since 0001 and never once put
                 on a screen. A replacement panel has to match the house, so
                 "white" versus "almond" is the difference between a job
                 that looks finished and one done twice. Off for auto,
                 because it has never been shown there and this is not the
                 change to start. -->
            ${w.hasColour ? `<div class="kv"><span>${esc(w.colourLabel)}</span><b>${
              vf("colour", job.vehicles.colour, "White, almond, brown")}</b></div>` : ""}
            <!-- ENGINE SIZE. Anthony asked for it beside year, make and
                 model, and it belongs there: two cars of the same year,
                 make and model take different parts and different hours
                 depending on what is under the hood, so a quote priced
                 without it is a quote priced on three quarters of a car.
                 A door does not have one, and an empty field somebody has
                 to wonder about is worse than no field. -->
            ${w.hasEngine ? `<div class="kv"><span>Engine</span><b>${
              vf("engine", job.vehicles.engine, "5.3L V8, 2.0T, diesel")}</b></div>` : ""}
            <div class="kv"><span>${esc(w.trimLabel)}</span><b>${
              vf("trim", job.vehicles.trim,
                 w.trimExample ?? (w.hasEngine ? "LT, Limited, Sport" : "Steel, wood, aluminium, glass"))}</b></div>
            ${w.hasPlate ? `<div class="kv"><span>Plate</span><b>${
              vf("license_plate", job.vehicles.license_plate, "Add the plate")}</b></div>` : ""}
            <!-- THE VIN FILLS IN THE REST. Seventeen characters off a door
                 jamb is one thing to type instead of three, and it is the
                 only one of these a person cannot get subtly wrong. -->
            <div class="kv"><span>${esc(cap(w.serial))}</span><b>${
              vf("vin", job.vehicles.vin, `Add the ${w.serial}`)}
              ${/* Decoding is a VIN lookup against a vehicle database. There
                    is nothing on the other end of it for a door serial, so
                    the button is not offered rather than offered and broken. */
                (w.hasEngine || w.hasPlate) && job.vehicles.vin
                  ? `<button class="linkbtn" id="vin-go">decode</button>` : ""}</b></div>
            <div id="vin-out"></div>
            ${w.reading ? `<div class="kv"><span>${esc(cap(w.reading))}</span><b>${
              vf("mileage", job.vehicles.mileage === null ? null : String(job.vehicles.mileage),
                 `Add the ${w.reading}`)}</b></div>` : ""}
            <p class="fine">${esc(w.whyItMatters)} Anybody but a read-only login
              can change any of these.</p>
          </div>
          <!-- ─── THIS ONE, EVERYWHERE ELSE ───────────────────────────
               The same record on other jobs, so a plate typed here is
               visibly the plate everywhere, and the manual side opened
               on this exact car rather than an empty shelf.

               Both were a line of small blue text in a kv row. They are
               the only two things to DO on this tab and they read as
               footnotes, so they are rows you can hit with a thumb now.
               #/vehicle/<id> went to the dashboard until 2026-09-20 --
               nothing served it. See vehiclePage in src/pages/shop.ts. -->
          <a class="card pad rowlink" href="#/vehicle/${esc(job.vehicles.id)}">
            <span class="rowlink-t">Everything done to this ${esc(w.thing)}</span>
            <span class="rowlink-s">Every job on this one, whoever owned it</span>
            <span class="rowlink-go">&rarr;</span>
          </a>
          ${w.hasPartsCatalog ? `
            <a class="card pad rowlink" href="${esc(manualsHref(job.vehicles))}"
               target="_blank" rel="noopener">
              <span class="rowlink-t">Manuals for this ${esc(w.thing)}</span>
              <span class="rowlink-s">${esc(manualsBlurb(job.vehicles, w.thing))}</span>
              <span class="rowlink-go">&rarr;</span>
            </a>` : ""}` : `
          <div class="card pad">
            <p class="quiet">There is no ${esc(w.thing)} on this job yet. Add it on the Details
              tab &mdash; the price comes off the ${esc(w.thing)}, so it is worth doing first.</p>
          </div>`}
      </div>

      ${job.status === "paid" ? `
        <!-- THE LAST STEP. A review is worth asking for in the hour after
             somebody has their keys back and is pleased, and worth nothing
             a week later. So it lives at the end of the job rather than on
             a marketing screen nobody opens. -->
        <div class="card pad reviewask">
          <div class="rowlink-t">Paid &mdash; ask them for a review</div>
          <p class="quiet">One text, sent from the shop's number, with the link.
             ${w.hasEngine ? "Best sent while they are still standing next to the car."
               /* Every other trade read "next to the car" too. Anthony,
                  2026-10-23, for SRC Remodel: "it better not say anything
                  about cars". */
               : "Best sent the day the work is done, while they are still pleased."}</p>
          ${reviewPick(job).url ? `
          <div class="ifin">
            ${canText() && job.customers?.phone
              ? `<button class="btn primary" id="ask-review">Send the review link</button>`
              : ""}
            <a class="btn ghost" href="${esc(reviewPick(job).url ?? "")}"
               target="_blank" rel="noopener">Open the page</a>
          </div>
          <p class="fine">${esc(reviewPick(job).label ?? "")} listing &mdash;
             ${esc(reviewPick(job).why)}.</p>` : `
          <p class="msg">Add your Google review link in
             <a href="#/settings">Settings</a> and this sends it. Until then
             there is no link to send.</p>`}
          <div id="review-out"></div>
        </div>` : ""}

      <div class="jpanel" data-panel="comms">
        <div class="card" id="jobcomms"></div>
      </div>

      <div class="jpanel" data-panel="money">
      <!-- ─── FIVE CARDS, NOT ONE COLUMN ──────────────────────────────
           This was a single card with seven sections stacked inside it
           and one head on the top, so folding it was all or nothing and
           nobody folded it. The deposit, the payments already taken and
           the resend are each a thing you go and look for; the figures
           and the box you type an amount into are the two you came for.
           So those two are open and the rest are shut, each head saying
           enough that a shut one is usually the end of it. -->
      <div class="card" data-fold="open">
        <div class="card-head">Money &middot; ${esc(moneyHead)}</div>
        <div class="pad">
          <!-- ─── THE SAME CAR IS ALREADY ON A BILL ──────────────────
               Shane Lee: one tire, two jobs, both signed, both $230.
               Billing the second would have asked him for $460. This
               does not stop anybody -- two repairs on one car in a
               fortnight is an ordinary thing -- it just refuses to let
               it happen without somebody having read the other bill. -->
          ${alsoBilled ? `
            <div class="billtwice">
              <b>${esc(alsoBilled.jobNumber)} already has a bill for this car.</b>
              ${esc(alsoBilled.invoiceNumber)} &middot; ${money(alsoBilled.total)}
              &middot; ${esc(alsoBilled.status === "paid" ? "paid" : "still owing")}
              &middot; raised ${esc(shopStamp(alsoBilled.issuedAt, false))}.
              <a href="#/job/${esc(alsoBilled.jobId)}">Open ${esc(alsoBilled.jobNumber)}</a>
              and check this is different work before billing it again.
            </div>` : ""}
          <!-- ─── A JOB THE SHOP IS NOT BILLING ────────────────────────
               Anthony: "equinox was a warranty job so no charge. add
               option."

               There was one shape for a job with no money on it and it was
               broken: the send button did not draw, the walk refused to
               start it because nothing was priced, and it could not close
               because closing wants an authorization, which is a customer
               agreeing to a figure that does not exist. The only ways out
               were to invent a price and not collect it, or leave the job
               open for ever.

               A reason rather than a tick, because warranty, goodwill and
               our own mistake are three different decisions that read the
               same in a total column. -->
          ${job.no_charge_reason
            ? `<div class="kv nocharge"><span>Not being billed</span>
                 <b>${esc(job.no_charge_reason)}
                   ${canMove ? `<button class="navlink" id="charge-after-all">Bill it after all</button>` : ""}
                 </b></div>`
            : `<div class="kv"><span>Job total</span><b data-jobtotal>${money(total)}</b></div>`}
          ${money$.depositPaid > 0.005 ? `<div class="kv"><span>${esc(cap(w.partsLabel))} deposit paid</span><b>&minus;${money(money$.depositPaid)}</b></div>` : ""}
          ${!job.no_charge_reason && canMove && !money$.invoiceId
            ? `<div class="kv"><span>Warranty or goodwill</span>
                 <b><button class="navlink" id="to-nocharge">Mark as no charge</button></b></div>`
            : ""}
          ${money$.invoiceId ? `
            <!-- THE BILL IS EDITABLE, and it has to be.

                 Anthony added a $150 line to job 1611 after the invoice
                 had gone out. Nothing recalculated the total, so the
                 customer's page said $950.15 while the lines added to
                 $1,100.15, and the only way to correct it was somebody
                 running SQL. A number a shop bills on that only an
                 engineer can change is not a number the shop owns.

                 A PAID invoice is not editable. That is settled money and
                 changing it would make the ledger disagree with what was
                 actually taken. -->
            <div class="kv"><span>Billed</span><b>${
              money$.paid > 0.005
                ? `${money(money$.total)}`
                : editable({
                    table: "invoices", id: money$.invoiceId, column: "total",
                    value: money$.total.toFixed(2), placeholder: "0.00",
                    editable: canEdit(role, "job.status" as Field),
                    reason: "Only a supervisor can change what was billed",
                    save: async (next) => {
                      const n = Number(String(next).replace(/[^0-9.]/g, ""));
                      if (!Number.isFinite(n) || n < 0) throw new Error("That is not an amount.");
                      await setInvoiceTotal(money$.invoiceId!, n);
                    },
                  })}
              <span class="quiet">&middot; ${esc(money$.invoiceNumber ?? "")}</span></b></div>
            <!-- WHEN THE BILL AND THE LINES DISAGREE, say so and offer the
                 one tap that fixes it. Silence here is what let 1611 sit
                 wrong; a person had no way of knowing the two numbers had
                 come apart. -->
            ${Math.abs((total - money$.depositPaid) - money$.total) > 0.005 && money$.paid <= 0.005 ? `
              <div class="billdrift">
                The lines on this job${money$.depositPaid > 0.005 ? ` less the ${money(money$.depositPaid)} deposit` : ""} add to <b>${money(total - money$.depositPaid)}</b>, which is
                ${money(Math.abs((total - money$.depositPaid) - money$.total))} ${(total - money$.depositPaid) > money$.total ? "more" : "less"}
                than the bill. ${esc(job.customers?.full_name ?? "They")} sees the bill.
                <button class="btn sm" id="rebill">Bill ${money(total - money$.depositPaid)}</button>
              </div>` : ""}
            <div class="kv"><span>Paid</span><b>${money(money$.paid)}</b></div>
            <div class="kv"><span>Balance</span><b class="${money$.balance > 0 ? "owed" : "settled"}">${
              money(money$.balance)}</b></div>
            <!-- Sending it to them lives in ONE place, further down this
                 same card: Text, Email or Both, with the chooser. A second
                 button up here shared its id with that one, so both
                 handlers bound to whichever the browser found first and
                 the chooser's own button did nothing at all. -->`
            : `<div class="quiet" style="padding:4px 0 8px">Not billed yet.</div>`}
        </div>
      </div>

      <!-- A PARTS DEPOSIT, before the work and before approval.
           Anthony (D6): "Quotes not yet approved with parts over
           $100: add ability to record parts deposit payment, reduce
           total owed at end of job, update customer-facing invoice
           to reflect deposit, and allow sending invoice to collect
           the deposit." Offered while there is no final bill and the
           parts on the quote come to more than $100; once taken it
           shows here and comes off the final invoice. -->
      ${!money$.invoiceId && (offersPrepay({ ppi: ppiJob, partsTotal: job.totals.parts_total,
          laborTotal: job.totals.labour_total, hasDeposit: Boolean(money$.deposit) })
          || (upFront && total > 0.005)) ? `
        <!-- A PREPAYMENT, for a pre-purchase inspection or any labor-only
             job. Anthony, 2026-10-07: inspections are paid before they are
             done, and with this card hidden below $100 of parts staff were
             marking them Done early to get a bill to put the money on. -->
        <div class="card" data-fold${ppiJob || upFront ? `="open"` : ""} id="prepay-card">
          <div class="card-head">${esc(depWord)} &middot; ${money$.deposit ? (money$.deposit.paid >= money$.deposit.total - 0.005
            ? "paid" : `${money(money$.deposit.total - money$.deposit.paid)} still to come`) : "none taken"}</div>
          <div class="pad">
              ${money$.deposit ? `
                <div class="kv"><span>Asked for</span><b>${money(money$.deposit.total)}
                  <span class="quiet">&middot; ${esc(money$.deposit.invoiceNumber)}</span></b></div>
                <div class="kv"><span>Received</span><b class="${money$.deposit.paid >= money$.deposit.total - 0.005 ? "settled" : "owed"}">${money(money$.deposit.paid)}</b></div>
                ${money$.deposit.paid < money$.deposit.total - 0.005 ? `
                  <div class="fine">Still waiting on ${money(money$.deposit.total - money$.deposit.paid)}. Record it below when it arrives, or send the request again.</div>
                  <div class="choice" id="dephow">
                    <button class="chip on" data-how="text">Text</button>
                    <button class="chip" data-how="email">Email</button>
                    <button class="chip" data-how="both">Both</button>
                  </div>
                  <button class="btn" id="depsend">${upFront ? "Send the payment link again" : `Send the ${esc(depWord.toLowerCase())} request again`}</button>`
                : `<div class="fine">Paid. It comes off the final bill${upFront ? "" : " when the work is done"}${
                    ppiJob ? ", so the inspection closes at nothing owed" : ""}.</div>`}`
              : `
                <div class="fine">${upFront
                  ? `Clients pay before the work starts. Once the agreement is signed, take ${money(total)}
                     here or send them the link. It comes off the final bill.`
                  : ppiJob
                  ? `A pre-purchase inspection is paid before it is done. Take ${money(total)} now; the job stays open
                     until it is inspected, and the final bill comes to nothing.`
                  : job.totals.parts_total > 100
                  ? `Parts on this quote come to ${money(job.totals.parts_total)}. Take a deposit
                  before ordering them. It comes off the final bill.`
                  : `Take payment before the work if it is due up front. It comes off the final bill and does
                     not close the job.`}</div>
                <div class="fine" style="margin-top:10px"><b>Already got it?</b> Record how it was paid:</div>
                <div class="choice" id="depmethod">
                  ${payMethods.map((m, i) => `
                    <button class="chip ${i === 0 ? "on" : ""}" data-m="${m}">${esc(METHOD_LABEL[m])}</button>`).join("")}
                </div>
                <div class="payin">
                  <input id="depamt" type="number" step="0.01" min="0" value="${
                    (job.totals.parts_total > 100 && !ppiJob && !upFront ? job.totals.parts_total : total).toFixed(2)}">
                  <button class="btn" id="deprecord">Record it as paid</button>
                </div>
                <div class="fine" style="margin-top:14px">Or ask the customer to pay it</div>
                <div class="choice" id="dephow">
                  <button class="chip on" data-how="text">Text</button>
                  <button class="chip" data-how="email">Email</button>
                  <button class="chip" data-how="both">Both</button>
                </div>
                <button class="btn primary" id="depsend">${upFront ? "Send them the payment link" : `Send them the ${esc(depWord.toLowerCase())} request`}</button>`}
              <div class="msg-slot" id="dep-msg"></div>
          </div>
        </div>` : ""}

      <!-- ─── WHAT WAS TAKEN, AND WHAT WAS TAKEN OFF ───────────────
           Anthony: "now the job 1645 has 150 recorded twice and the
           customer only paid 150 total."

           Two people recorded the same $150 fifty-seven minutes
           apart. Nothing on this card said who entered either, and
           nothing anywhere could remove one -- the trigger that
           settles an invoice only ever fired on INSERT, so money went
           up and never down. See migration 0092.

           A voided row STAYS, struck through with the reason. A shop
           with a BAR license does not delete money records, and
           "there were two entries and one was a mistake" is the
           answer to a question somebody asks in eighteen months. -->
      ${money$.payments.length ? `
        <div class="card" data-fold>
          <div class="card-head">What was taken &middot; ${money$.payments.filter((p) => !p.voided_at).length}</div>
          <div class="pad">
          <div class="paylist">${money$.payments.map((pm) => `
            <div class="payrow${pm.voided_at ? " void" : ""}">
              <span>${esc(METHOD_LABEL[pm.method] ?? pm.method)}${
                pm.reference ? ` &middot; ${esc(pm.reference)}` : ""}
                <!-- The date, then how long ago, then who. A blank
                     recorder on a card is not missing data: it means the
                     customer paid it themselves, and it says so. -->
                <i class="paywhen">${esc(shopStamp(pm.received_at))}${
                  pm.received_at ? ` &middot; ${esc(since(pm.received_at))}` : ""}${
                  pm.recordedByName ? ` &middot; taken by ${esc(pm.recordedByName)}`
                    : pm.method === "card" ? " &middot; paid by the customer online" : ""
                }</i></span>
              <b>${money(pm.amount)}</b>
              ${pm.voided_at
                ? `<span class="payvoid">Taken off &middot; ${esc(pm.void_reason ?? "no reason given")}</span>`
                : canMove
                ? `<button class="navlink quiet payoff" data-void="${esc(pm.id)}">Take it off</button>`
                : ""}
            </div>`).join("")}</div>
          </div>
        </div>` : ""}

      ${money$.balance > 0.005 || !money$.invoiceId ? `
        <div class="card" data-fold="open">
          <div class="card-head">Take a payment</div>
          <div class="pad">
          <div class="paytake">
              <div class="choice" id="paymethod">
                ${payMethods.map((m, i) => `
                  <button class="chip ${i === 0 ? "on" : ""}" data-m="${m}">${esc(METHOD_LABEL[m])}</button>`).join("")}
              </div>
              <div class="payin">
                <!-- A prepaid inspection is not owed twice: on a PPI the box
                     opens on what is still to come, not the whole price. -->
                <input id="payamt" type="number" step="0.01" min="0"
                       value="${(money$.invoiceId ? money$.balance
                         : ppiJob ? (money$.deposit && money$.deposit.paid < money$.deposit.total - 0.005
                             ? money$.deposit.total - money$.deposit.paid : money$.balance)
                         : total).toFixed(2)}">
                <input id="payref">
                <button class="btn primary" id="paysave">Record it</button>
              </div>
              <!-- PART OF IT IS A NORMAL DAY. Anthony: "Sometimes
                   customers pay a down payment or like for the diag but
                   not the whole job the first day we bill."

                   Taking less than the balance has always worked -- the
                   box is a number and the invoice only settles when the
                   payments reach the total. Nothing said so, and the box
                   arrives pre-filled with the whole amount, which reads
                   as the only figure it will accept. This says it, and
                   says what would still be owed as they type. -->
              <div class="fine" id="partsay">Type a smaller figure to take part of it.
                ${money$.invoiceId ? `The balance is ${money(money$.balance)}.` : ""}</div>
              ${!money$.invoiceId && ppiJob && !["completed", "invoiced", "paid"].includes(job.status)
                ? `<div class="fine">This inspection is not done yet, so whatever you record here is a
                  prepayment: it goes on the ${esc(depWord)} and the job stays open until it is inspected.</div>`
              : !money$.invoiceId ? `<div class="fine">There is no invoice on this job yet — recording a
                payment raises one first, because "they paid cash for a job nobody billed" is the
                commonest shape in the old data and two steps is how it goes unrecorded twice.</div>` : ""}
              ${!configNow().features.payments ? `<div class="fine">Card payments through the
                gateway are off on this deployment — there is no payment key here, on purpose.
                Recording a card taken on the shop's own terminal still works.</div>` : ""}
          </div>
          </div>
        </div>` : `<div class="card pad"><div class="settledbox">Settled in full.</div></div>`}

      ${money$.invoiceId ? `
        <!-- SEND IT AGAIN. Anthony: "I should have an option to send a
             customer the invoice if they didn't receive it and select
             text + email or one or the other." A bill that did not
             arrive is a bill nobody is paying, and until now the only
             way to resend one was to raise another. -->
        <div class="card" data-fold>
          <div class="card-head">Send them the invoice</div>
          <div class="pad">
          <div class="resend">
              <div class="choice" id="sendhow">
                <button class="chip on" data-how="text">Text</button>
                <button class="chip" data-how="email">Email</button>
                <button class="chip" data-how="both">Both</button>
              </div>
              <button class="btn" id="send-invoice">Send it</button>
              <div class="msg-slot" id="send-msg"></div>
          </div>
          </div>
        </div>` : ""}
      <div class="msg-slot" id="pay-msg"></div>
      </div><!-- /money -->

      <div class="jpanel" data-panel="history">
      <!-- NOTES FIRST, because it is the only thing on this panel somebody
           came here to WRITE. Anthony: "put add note under history."
           Everything below it is the record; this is where the record gets
           added to. -->
      <div class="card" id="job-notes"></div>

      <!-- THIS CAR AND THIS CUSTOMER, BEFORE TODAY. An advisor asking "have
           we had this in before" was opening the customer record in another
           tab to find out. -->
      <div class="card" data-fold>
        <div class="card-head">Their other work &middot; ${theirs.length}</div>
        ${theirs.length ? theirs.map((o) => `
          <a class="row" href="#/job/${esc(o.id)}">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(o.job_number)}</span>
                <span class="pill s-${esc(o.status)}">${
                  esc(STATUS_LABEL[o.status] ?? o.status)}</span></div>
              <div class="row-sub">${esc(o.title || o.complaint || "No description")}</div>
            </div>
            <div class="row-side"><div class="amount">${money(o.total)}</div>
              <div class="when">${esc(since(o.created_at))}</div></div>
          </a>`).join("")
          : `<div class="empty">This is their first job with the shop.</div>`}
      </div>

      <!-- THE AUTHORIZATIONS, WITH THE INK.
           Anthony: "notes needs to have a record of signature or record
           somewhere on the card."

           The band at the top says one line about the most recent yes.
           This is the file copy: every figure that was ever agreed to,
           who agreed, by what means, and the drawing itself where there
           is one. It is what gets shown to somebody who says they never
           approved the work, so it renders the signature rather than
           merely reporting that one exists. -->
      <div class="card" id="authorizations" data-fold>
        <div class="card-head">What they authorized &middot; ${live.length}</div>
        ${live.length ? live.map((a) => `
          <div class="authrec">
            <div class="authrec-h">
              <b>${money(a.amount)}</b>
              <span class="pill">${esc(
                a.method === "portal" ? "signed on their screen"
                : a.method === "phone" ? "over the phone"
                : a.method === "counter" ? "at the counter" : "in person")}</span>
              ${a.kind !== "initial" ? `<span class="pill">${esc(a.kind)}</span>` : ""}
            </div>
            <div class="authrec-w">${esc(a.authorized_by)}
              <span class="when">${esc(since(a.authorized_at))}</span></div>
            ${(a.signature ?? "").startsWith("data:image/")
              ? `<img class="authrec-sig" alt="Signature of ${esc(a.authorized_by)}"
                      src="${esc(a.signature ?? "")}">`
              : `<div class="fine">No signature, and none is required &mdash;
                   who agreed and when is the record.</div>`}
          </div>`).join("")
          : `<div class="empty">Nobody has authorized anything on this job.</div>`}
        ${auths.some((a) => a.declined) ? `<div class="authrec dec">
          ${auths.filter((a) => a.declined).map((a) => `
            <div class="authrec-w">${esc(a.authorized_by)} declined ${money(a.amount)}
              <span class="when">${esc(since(a.authorized_at))}</span></div>`).join("")}
        </div>` : ""}
      </div>

      <!-- ─── WHAT THE GAUGES SAID ──────────────────────────────────
           Only on a job that has air conditioning work on it. A card of
           empty pressure boxes on a brake job is four things somebody
           has to learn to ignore, and a nag learned is a nag dead. -->
      ${acWanted ? `<div class="card" data-fold>
        <div class="card-head">Air conditioning readings</div>
        <div class="kv"><span>High side</span>
          <b>${acField("ac_high_psi", job.ac_high_psi)} <span class="quiet">psi</span></b></div>
        <div class="kv"><span>Low side</span>
          <b>${acField("ac_low_psi", job.ac_low_psi)} <span class="quiet">psi</span></b></div>
        <div class="kv"><span>Center outlet</span>
          <b>${acField("ac_vent_f", job.ac_vent_f)} <span class="quiet">&deg;F</span></b></div>
        <div class="kv"><span>Outside air</span>
          <b>${acField("ac_ambient_f", job.ac_ambient_f)} <span class="quiet">&deg;F</span></b></div>
        ${acWarn ? `<p class="msg">${esc(acWarn)}</p>` : ""}
        <p class="quiet small">Outside air is not required, and the other three
           cannot be read without it a year from now. A reading of 0 is a real
           reading &mdash; type it rather than leaving the box empty.</p>
      </div>` : ""}

      <!-- ─── TAKING IT APART ───────────────────────────────────────
           A teardown is agreed to before anybody knows the price of the
           repair, on five disclosures of its own. Its own card, because
           it is its own conversation and it happens at a different
           moment from the one above. See src/domain/teardown.ts. -->
      <div class="card" data-fold>
        <div class="card-head">${esc(TEARDOWN_TITLE)}</div>
        ${teardownYes ? `
          <div class="authrec">
            <div class="authrec-h">
              <b>${money(teardownYes.amount)}</b>
              <span class="pill">teardown agreed</span>
            </div>
            <div class="authrec-w">${esc(teardownYes.authorized_by)}
              <span class="when">${esc(since(teardownYes.authorized_at))}</span></div>
            <ul class="fine td-said">${
              disclosureLines(teardownOf(teardownYes as unknown as Record<string, unknown>), money)
                .map((s) => `<li>${esc(s)}</li>`).join("")}</ul>
          </div>`
        : `<div class="empty">Nothing on this job has been taken apart by agreement.</div>
           ${mayRecordHere ? `<div class="af-row">
             <button class="btn ghost sm" id="td-open">Record a teardown authorization</button>
           </div>` : ""}
           <div id="td-host"></div>`}
      </div>

      <div class="card" data-fold>
        <div class="card-head">What has happened &middot; ${events.length}</div>
        ${events.length ? `<ul class="timeline">${events.map((e) => `
          <li><b>${esc(e.title)}</b>
            <span class="who">${esc(e.actor)}</span>
            <span class="when">${esc(since(e.created_at))}</span>
            ${e.detail ? `<div class="detail">${esc(e.detail)}</div>` : ""}</li>`).join("")}</ul>`
          : `<div class="empty">Nothing recorded yet.</div>`}
      </div>
      </div><!-- /history -->

      <!-- ─── THE BAND, UNDER THE PANEL IT USED TO INTERRUPT ─────────
           Anthony: "I want the six tabs where they are yes but
           immediately under them I want the corresponding container
           that loads."

           The stars, the track, what is next and the authorization used
           to sit BETWEEN the tabs and their content, so tapping The work
           moved you to a screen whose work was four scrolls down. A tab
           and the thing it opens have to touch.

           It is still on every tab rather than inside one, because where
           the job is and what to do next is true whichever drawer is
           open. It is simply no longer in the way of the answer. -->
      <div class="card pad">${starRow}${track}${nextBlock}
        ${needsAuth ? `
          <!-- Folded on a phone. Four fields and two buttons at the top of
               the card pushed the stars, the track and the next step off
               the screen, so the one thing a tech opens the job to read
               was below the fold. It stays at the TOP and stays loud —
               a missing authorization is not something to bury — but as
               one line until somebody is actually recording it. -->
          <details class="authwrap" id="authwrap"${roomy ? " open" : ""}>
            <summary>
              <span class="aw-dot"></span>
              <span class="aw-t">No authorization on this job yet</span>
              <span class="aw-go">Record the yes</span>
            </summary>
            <div id="authhost"></div>
          </details>` : ""}</div>

      <!-- ─── THEIR PAGE ───────────────────────────────────────────────
           Anthony: "still need a link to customer page in the crm and
           option to send to them. put at very bottom of card so we don't
           have to troubleshoot collision or anything."

           OUTSIDE the tabs on purpose, at the foot of the job, so it is
           in the same place whichever tab happens to be open and cannot
           collide with anything inside one. Every id here is prefixed
           cp- for the same reason: a duplicate id cost us a dead button
           earlier tonight, when two Send buttons shared one and the
           browser handed both handlers to whichever it found first.

           OPEN IT is not decoration. It is the only way to see what the
           customer sees before sending it to them, and it is how you
           check a link works rather than finding out when they say it
           does not. -->
      <div class="card custpage" data-fold>
        <div class="card-head">Their page</div>
        <div class="pad">
          ${job.customers ? `
            <p class="fine">Everything this customer can see: the price, the
              photos, the inspection, and the button that approves it. No app,
              no login.</p>
            <div class="cp-link" id="cp-link">Making their link&hellip;</div>
            <div class="cp-acts">
              <a class="btn" id="cp-open" href="#" target="_blank"
                 rel="noopener">Open it</a>
              <button class="btn ghost" id="cp-copy">Copy the link</button>
            </div>
            <div class="card-sub" style="margin-top:14px">Send them the link</div>
            <div class="choice" id="cp-how">
              <button class="chip on" data-how="text">Text</button>
              <button class="chip" data-how="email">Email</button>
              <button class="chip" data-how="both">Both</button>
            </div>
            <!-- ─── THE OTHER BUTTON IS THE ONE THEY MEANT ──────────────
                 Anthony: "two adjacent buttons ... where pressing the wrong
                 one leaves no trace that you meant to send a quote."

                 Matt Bolen sent both his customers this link and neither
                 got a quote. One of them is holding a working approve-and-
                 sign page for $1,455.73 and has never been told the price.
                 Nothing anywhere said the quote had not gone, because from
                 the system's point of view nothing went wrong: a link was
                 requested and a link was sent.

                 So the button says what it sends, and when there is a
                 price on the job that has never been quoted, this card
                 says so before the button rather than after. -->
            ${total > 0 && !job.quote_expires_at ? `
              <p class="cp-warn">This sends the link, not the price. The quote
                for ${money(total)} has not been sent yet &mdash;
                <button class="linkbtn" id="cp-to-quote">send the quote instead</button>.</p>` : ""}
            <!-- ─── AND THE BILL THAT WAS NEVER RAISED ───────────────────
                 Job 1753, 19 September. The work was finished at 1:34 and
                 this link went out at 1:31 with nothing billed behind it,
                 so Derrick opened his page, read "Total $147.50" and had
                 no button to press. He rang the shop and paid by Zelle.

                 Same shape as the warning above, and the same remedy: say
                 it before the button rather than after, and make noticing
                 it and fixing it the same click. -->
            ${finishedUnbilled ? `
              <p class="cp-warn">This sends the link, not a bill. Nothing is
                billed on this job yet, so their page will show ${money(total)}
                with no way to pay it &mdash;
                <button class="linkbtn" id="cp-to-bill">raise the invoice first</button>.</p>` : ""}
            <button class="btn primary" id="cp-send">Send the link to their page</button>
            <div class="msg-slot" id="cp-said"></div>`
            : `<p class="quiet">No customer on this job, so there is nobody to
                 send a page to.</p>`}
        </div>
      </div>
    </div>`);

  wireTabs(id);
  /* Last, so anything that queries inside these cards has already run.
     A shut card's contents are still in the document -- folding hides
     them, it does not remove them. See src/ui/fold.ts. */
  foldCards();

  /**
   * THEIR PAGE: show the link, open it, copy it, send it.
   *
   * The token is minted on demand rather than up front, because most job
   * screens are opened to read something and a customer who never gets a
   * link does not need one made for them.
   */
  void (async () => {
    const cust = job!.customers;
    if (!cust) return;
    const box = document.getElementById("cp-link");
    const open = document.getElementById("cp-open") as HTMLAnchorElement | null;
    try {
      const tok = await ensurePortalToken(cust.id);
      if (!tok) throw new Error("could not make a link");
      const link = `${customerSite()}/garage.html?t=${encodeURIComponent(tok)}`;
      if (box) box.textContent = link;
      if (open) open.href = link;

      document.getElementById("cp-copy")?.addEventListener("click", async () => {
        const b = document.getElementById("cp-copy") as HTMLButtonElement;
        try {
          await navigator.clipboard.writeText(link);
          b.textContent = "Copied";
        } catch {
          // Clipboard is refused on an insecure origin and in some
          // in-app browsers. The link is on screen either way, so say
          // so rather than pretending it worked.
          b.textContent = "Select it above";
        }
        setTimeout(() => { b.textContent = "Copy the link"; }, 2200);
      });
    } catch {
      if (box) box.textContent = "Could not make a link for this customer.";
      open?.remove();
      document.getElementById("cp-copy")?.remove();
    }
  })();

  let cpHow: "text" | "email" | "both" = "text";
  document.querySelectorAll<HTMLButtonElement>("#cp-how .chip").forEach((b) =>
    b.addEventListener("click", () => {
      cpHow = (b.dataset["how"] ?? "text") as typeof cpHow;
      document.querySelectorAll("#cp-how .chip").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
    }));

  document.getElementById("cp-send")?.addEventListener("click", async () => {
    const b = document.getElementById("cp-send") as HTMLButtonElement;
    const said = document.getElementById("cp-said");
    const cust = job!.customers;
    const tell = (html: string) => { if (said) said.innerHTML = html; };

    if (!cust) return;
    if ((cpHow === "email" || cpHow === "both") && !cust.email) {
      tell(`<p class="msg bad">${esc(cust.full_name ?? "They")} has no email on
        file. Add one on Details, or send it as a text.</p>`);
      return;
    }
    if ((cpHow === "text" || cpHow === "both") && !cust.phone) {
      tell(`<p class="msg bad">No number on file, so it cannot be texted.</p>`);
      return;
    }

    b.disabled = true; b.textContent = "Sending\u2026";
    const first = (cust.full_name ?? "").split(" ")[0] || "there";
    const done: string[] = [];
    const failed: string[] = [];
    try {
      const tok = await ensurePortalToken(cust.id);
      const link = `${customerSite()}/garage.html?t=${encodeURIComponent(tok ?? "")}`;
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const auth = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      };

      if (cpHow === "text" || cpHow === "both") {
        const r = await fetch(api("/api/sms/send"), {
          method: "POST", headers: auth,
          body: JSON.stringify({
            customer_id: cust.id, job_id: id, template: true,
            /* carPhrase, not vehicleName: a label reads "No vehicle" in a
               sentence, and a lock or PI customer has no car at all. */
            message: `Hi ${first}, here's your page with everything on `
              + `${carPhrase(job!.vehicles, { trade: currentTrade() })}: ${link}`,
          }),
        });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(
          r.ok && out.ok ? "texted" : `text failed: ${out.error ?? r.status}`);
      }

      if (cpHow === "email" || cpHow === "both") {
        const r = await fetch(api("/api/email/send"), {
          method: "POST", headers: auth,
          body: JSON.stringify({
            customer_id: cust.id, job_id: id,
            subject: `Your page at ${shopName()}`,
            message: `Hi ${first},\n\nEverything on your `
              + `${vehicleName(job!.vehicles)} is here — the price, the photos, `
              + `and the inspection.\n${link}\n\nAny questions, just reply.`,
          }),
        });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(
          r.ok && out.ok ? "emailed" : `email failed: ${out.error ?? r.status}`);
      }

      b.disabled = false; b.textContent = "Send the link to their page";
      tell([
        done.length ? `<p class="msg ok">Sent &mdash; ${esc(done.join(" and "))}.</p>` : "",
        failed.length ? `<p class="msg bad">${esc(failed.join("; "))}</p>` : "",
      ].join(""));
      if (cust) void customerComms("jobcomms", cust.id, cust.phone ?? null);
    } catch (e) {
      b.disabled = false; b.textContent = "Send the link to their page";
      tell(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
    }
  });

  /**
   * ONE TAP TO MAKE THE BILL MATCH THE WORK.
   *
   * The Billed figure is editable, but typing $1,100.15 by hand when the
   * lines already say $1,100.15 is asking somebody to be a calculator.
   * This is the same fix without the arithmetic.
   */
  document.getElementById("rebill")?.addEventListener("click", async () => {
    const b = document.getElementById("rebill") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Billing…";
    try {
      await setInvoiceTotal(money$.invoiceId!, total - money$.depositPaid);
      void jobPage(id, role);
    } catch (e) {
      b.disabled = false;
      b.textContent = e instanceof Error ? e.message : "That did not work";
    }
  });

  bindEditables();
  /* A restoration company's loss file. Reads its own column and hides
     itself if 0235 is not applied. See src/pages/restoration.ts. */
  if (w.lossFile) void mountLossFile("lossfile", job, role);
  // After the card is in the DOM, not before — the page has no height
  // to scroll to until then.
  if (wasAt > 0) window.scrollTo({ top: wasAt });

  // The quote itself, editable in place. Tiers only suggest.
  //
  // The rate comes from the shop, or from the job when that job carries
  // its own — work quoted at last year's rate keeps it. It used to be the
  // literal 150, which made the settings screen decoration.
  void Promise.all([loadTiers().catch(() => [] as Tier[]), shopLabourRate()])
    .then(([tiers, shopRate]) => {
      const rate = Number(job!.labour_rate) > 0 ? Number(job!.labour_rate) : shopRate;
      lineEditor("lineedit", id, lines!, tiers, rate, () => void jobPage(id, role), {
        vehicle: vehicleName(job!.vehicles),
        complaint: job!.complaint ?? "",
        // humanNotes, never the raw column: old jobs still carry the
        // [tech tests] JSON fence in there and a wall of braces is not a
        // diagnosis.
        //
        // The diagnostic card leads (0188), then the notes. The card is
        // what the desk quotes from, so the drafter reads it first rather
        // than having to find a tagged line in a paragraph of gate codes.
        // Old jobs with only the tag still carry it inside the notes, and
        // diagnosisOf() would then repeat it, so the card is added only
        // when it is its own column.
        diagnosis: [
          job!.diagnosis?.trim() ? `Diagnostic card: ${diagnosisOf(job!)}` : "",
          humanNotes(job!.notes),
        ].filter(Boolean).join("\n\n"),
      }, (t) => {
        // An edit patches the figures in place rather than rebuilding the
        // card under the person typing.
        const big = document.getElementById("job-total-big");
        if (big) big.textContent = money(t);
        document.querySelectorAll<HTMLElement>("[data-jobtotal]")
          .forEach((el) => { el.textContent = money(t); });
      });
    });

  const say = (e: unknown) => {
    const m = document.getElementById("stage-msg");
    if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  };

  let payMethod: Method = "cash";
  document.querySelectorAll<HTMLButtonElement>("#paymethod .chip").forEach((b) =>
    b.addEventListener("click", () => {
      document.querySelectorAll("#paymethod .chip").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      payMethod = b.dataset["m"] as Method;
    }));

  /**
   * TAKING A PAYMENT OFF. Two taps and a reason, because it changes what
   * a customer owes and it is the one money action nobody should do by
   * brushing past a button on a phone.
   */
  const payMsg = (html: string): void => {
    const m = document.getElementById("pay-msg");
    if (m) m.innerHTML = html;
  };

  document.querySelectorAll<HTMLButtonElement>("[data-void]").forEach((btn) =>
    btn.addEventListener("click", async () => {
      const payId = btn.dataset["void"] ?? "";
      const why = prompt("Why is this coming off? It stays on the record with your name.");
      if (why === null) return;
      if (why.trim().length < 4) {
        payMsg(`<p class="msg bad">Say why it is being taken off.</p>`);
        return;
      }
      btn.disabled = true; btn.textContent = "Taking it off…";
      try {
        await voidPayment(payId, why);
        /* The invoice and the job put themselves right by trigger, so the
           card is redrawn rather than patched -- the status above it has
           very likely just changed too. */
        void jobPage(id, role);
      } catch (e) {
        btn.disabled = false; btn.textContent = "Take it off";
        payMsg(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
      }
    }));

  /* What is still owed, worked out as they type. */
  const partSay = (): void => {
    const box = document.getElementById("payamt") as HTMLInputElement | null;
    const say = document.getElementById("partsay");
    if (!box || !say || !money$.invoiceId) return;
    const n = Number(box.value);
    const left = Math.round((money$.balance - (Number.isFinite(n) ? n : 0)) * 100) / 100;
    say.innerHTML = left > 0.005
      ? `That leaves <b>${esc(money(left))}</b> owing after this one.`
      : left < -0.005
      ? `That is <b>${esc(money(-left))}</b> more than the balance.`
      : `That settles it in full.`;
  };
  document.getElementById("payamt")?.addEventListener("input", partSay);
  partSay();

  document.getElementById("paysave")?.addEventListener("click", async () => {
    const btn = document.getElementById("paysave") as HTMLButtonElement | null;
    const amt = Number((document.getElementById("payamt") as HTMLInputElement | null)?.value ?? 0);
    const ref = (document.getElementById("payref") as HTMLInputElement | null)?.value ?? "";
    if (btn) { btn.disabled = true; btn.textContent = "Recording…"; }
    try {
      // With no final bill yet and a deposit still owed, the money lands
      // on the deposit; otherwise on the bill, raised if it has to be.
      const dep = money$.deposit;
      const onDeposit = !money$.invoiceId && dep && dep.status === "open" && dep.paid < dep.total - 0.005;
      await recordPayment(id, amt, payMethod, ref, onDeposit ? dep!.invoiceId : undefined);
      void jobPage(id, role);
    } catch (e) {
      const m = document.getElementById("pay-msg");
      if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      if (btn) { btn.disabled = false; btn.textContent = "Record it"; }
    }
  });

  // ── the parts deposit ────────────────────────────────────────────────
  let depHow: "text" | "email" | "both" = "text";
  document.querySelectorAll<HTMLButtonElement>("#dephow .chip").forEach((b) =>
    b.addEventListener("click", () => {
      document.querySelectorAll("#dephow .chip").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      depHow = b.dataset["how"] as typeof depHow;
    }));

  // The deposit records with its OWN "paid by" selector, not the one in
  // Take a payment below -- a deposit taken on a card and one taken in cash
  // are different records, and the box must say which.
  let depMethod: Method = "cash";
  document.querySelectorAll<HTMLButtonElement>("#depmethod .chip").forEach((b) =>
    b.addEventListener("click", () => {
      document.querySelectorAll("#depmethod .chip").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      depMethod = b.dataset["m"] as Method;
    }));

  const depSay = (html: string) => { const m = document.getElementById("dep-msg"); if (m) m.innerHTML = html; };

  document.getElementById("deprecord")?.addEventListener("click", async () => {
    const btn = document.getElementById("deprecord") as HTMLButtonElement;
    const amt = Number((document.getElementById("depamt") as HTMLInputElement | null)?.value ?? 0);
    btn.disabled = true; btn.textContent = "Recording…";
    try {
      /* A PPI's prepayment is asked for at the inspection's price, so a
         part of it recorded now leaves the rest showing as owed. */
      const depId = await raiseDepositInvoice(id, ppiJob && total > amt ? total : amt);
      await recordPayment(id, amt, depMethod, ppiJob ? "Prepayment" : upFront ? depWord : `${cap(shopWords().partsLabel)} deposit`, depId);
      void jobPage(id, role);
    } catch (e) {
      btn.disabled = false; btn.textContent = "Record it as paid";
      depSay(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
    }
  });

  document.getElementById("depsend")?.addEventListener("click", async () => {
    const btn = document.getElementById("depsend") as HTMLButtonElement;
    const cust = job!.customers;
    const first = (cust?.full_name ?? "").split(" ")[0] || "there";
    const amt = money$.deposit
      ? money$.deposit.total
      : Number((document.getElementById("depamt") as HTMLInputElement | null)?.value ?? 0);
    if (!(amt > 0)) { depSay(`<p class="msg bad">How much is the deposit?</p>`); return; }
    if ((depHow === "email" || depHow === "both") && !cust?.email) {
      depSay(`<p class="msg bad">${esc(cust?.full_name ?? "They")} has no email address on file.</p>`); return;
    }
    if ((depHow === "text" || depHow === "both") && !cust?.phone) {
      depSay(`<p class="msg bad">No mobile number on file, so it cannot be texted.</p>`); return;
    }
    const tok = cust ? await ensurePortalToken(cust.id) : null;
    if (!tok) { depSay(`<p class="msg bad">Could not make a link for this customer.</p>`); return; }
    btn.disabled = true; btn.textContent = "Sending…";
    try {
      await raiseDepositInvoice(id, amt);
      const link = `${customerSite()}/garage.html?t=${encodeURIComponent(tok)}`;
      const car = vehicleName(job!.vehicles);
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const auth = { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` };
      const done: string[] = []; const failed: string[] = [];
      if (depHow === "text" || depHow === "both") {
        const r = await fetch(api("/api/sms/send"), { method: "POST", headers: auth, body: JSON.stringify({
          customer_id: cust!.id, job_id: id, template: true,
          message: ppiJob
            /* The same words the automatic PPI quote and the MechanicRank
               bridge send, so a customer hears one sentence from us. */
            ? ppiPayText({ first, car: job!.vehicles ? car : "", amount: amt, link })
            : upFront
            ? `Hi ${first}, thank you for signing. Payment of ${money(amt)} is due before we begin. You can pay it here. ${link}`
            : `Hi ${first}, to order the parts for ${carPhrase(job!.vehicles, { trade: currentTrade() })} we take a parts deposit of ${money(amt)}. `
            + `It comes off your final bill. You can pay it here. ${link}` }) });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(r.ok && out.ok ? "texted" : `text failed: ${out.error ?? r.status}`);
      }
      if (depHow === "email" || depHow === "both") {
        const r = await fetch(api("/api/email/send"), { method: "POST", headers: auth, body: JSON.stringify({
          customer_id: cust!.id, job_id: id,
          subject: ppiJob ? `Your pre-purchase inspection: approve and pay`
            : upFront ? `Payment for your ${shopWords().thing}` : `${cap(shopWords().partsLabel)} deposit for your ${car}`,
          message: ppiJob
            ? `Hi ${first},\n\nYour pre-purchase inspection${job!.vehicles ? ` on the ${car}` : ""} is ${money(amt)}. `
              + `Approve and pay here to lock in your inspection:\n${link}\n\nAny questions, just reply.`
            : upFront
            ? `Hi ${first},\n\nThank you for signing. Payment of ${money(amt)} is due before we begin.\n\nYou can pay it here.\n${link}\n\nAny questions, just reply.`
            : `Hi ${first},\n\nTo order the ${shopWords().partsLabel} for your ${car} we take a ${shopWords().partsLabel} deposit of ${money(amt)}. `
            + `It comes off your final bill.\n\nYou can pay it here.\n${link}\n\nAny questions, just reply.` }) });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(r.ok && out.ok ? "emailed" : `email failed: ${out.error ?? r.status}`);
      }
      depSay([done.length ? `<p class="msg ok">Sent &mdash; ${esc(done.join(" and "))}.</p>` : "",
        failed.length ? `<p class="msg bad">${esc(failed.join("; "))}</p>` : ""].join(""));
      if (cust) void customerComms("jobcomms", cust.id, cust.phone ?? null);
      if (!money$.deposit) setTimeout(() => void jobPage(id, role), 1200);
      else { btn.disabled = false; btn.textContent = "Send the deposit request again"; }
    } catch (e) {
      btn.disabled = false; btn.textContent = "Send them the deposit request";
      depSay(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
    }
  });

  const guardNow = () => ({
    hasAuthorization: yesOnFile,
    hasPricedLine: lines!.some((l) => Number(l.unit_price) > 0),
    // A warranty job has no figure to agree to and no priced line. See 0088.
    noCharge: Boolean(job!.no_charge_reason),
  });

  /**
   * THE ONE BUTTON THAT MOVES THE JOB. Same path as a stage chip, gate and
   * all — the shortcut is on the finding, never on the checking.
   */
  document.getElementById("movebtn")?.addEventListener("click", async () => {
    const b = document.getElementById("movebtn") as HTMLButtonElement;
    const m = document.getElementById("move-msg");
    const label = b.textContent ?? "";
    const mv = step.move;
    if (!mv) return;
    b.disabled = true; b.textContent = "Working…";
    try {
      if (mv.kind === "book") {
        // Nothing to set from here — the date is a decision, so this puts
        // the person in the box rather than inventing a time for them.
        b.disabled = false; b.textContent = label;
        /* THE BOX IS TWO BOXES. It was one datetime-local called "appt"
           and became a date and a time side by side, which reads far
           better on a phone -- but the one button that sends somebody to
           it kept asking for the old id. getElementById returned null,
           optional chaining swallowed it, and the big primary "Book it"
           on every approved job scrolled nowhere and focused nothing.
           Exactly the shape of glitch the smoke test now looks for. */
        const box = document.getElementById("appt-date") as HTMLInputElement | null;
        box?.scrollIntoView({ behavior: "smooth", block: "center" });
        box?.focus();
        // Safari has no showPicker, and it throws where the box is not
        // yet on screen. Neither is a failure worth telling anybody about
        // — the field is focused either way.
        try { if (box && !box.value) box.showPicker?.(); } catch { /* focused anyway */ }
        return;
      }
      if (mv.kind === "authorize") {
        // The form is on this card already, folded shut on a phone so it
        // does not push the stars off the screen. Open it and go there.
        b.disabled = false; b.textContent = label;
        const w = document.getElementById("authwrap") as HTMLDetailsElement | null;
        if (w) w.open = true;
        w?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (mv.kind === "prepay") {
        // The prepayment card lives on the money tab, open on a PPI.
        b.disabled = false; b.textContent = label;
        document.querySelector<HTMLButtonElement>('.jtab[data-tab="money"]')?.click();
        const card = document.getElementById("prepay-card");
        card?.scrollIntoView({ behavior: "smooth", block: "center" });
        (document.getElementById("depamt") as HTMLInputElement | null)?.focus();
        return;
      }
      if (mv.kind === "pay") {
        b.disabled = false; b.textContent = label;
        const amt = document.getElementById("payamt");
        (amt ?? document.querySelector(".paytake"))
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        (amt as HTMLInputElement | null)?.focus();
        return;
      }
      if (mv.kind === "price") {
        // The price comes from the line editor, so this walks them to it
        // rather than inventing a figure. Same reasoning as `book`.
        b.disabled = false; b.textContent = label;
        const host = document.getElementById("lineedit");
        host?.scrollIntoView({ behavior: "smooth", block: "center" });
        (host?.querySelector("[data-add='labour']") as HTMLElement | null)?.focus();
        return;
      }
      if (mv.kind === "invoice") {
        await raiseInvoice(id);
        // Paid ahead in full, it closed itself at $0; do not drag it back.
        if (!await closedByPrepayment(id)) await setStatus(id, "invoiced", guardNow());
        void jobPage(id, role);
        return;
      }
      await setStatus(id, mv.to, guardNow());
      void jobPage(id, role);
    } catch (e) {
      b.disabled = false; b.textContent = label;
      const missed = (e as Error & { protocol?: string[] }).protocol;
      if (!missed) {
        if (m) {
          m.innerHTML = `<p class="msg bad">${
            esc(e instanceof Error ? e.message : String(e))}</p>`;
        }
        return;
      }
      // `book` returns before it can throw, so the only kinds that reach
      // here are the two that name a status.
      // Everything that only moves the screen returns before it can throw,
      // so the only kinds that reach here are the two that name a status.
      askOverride(mv.kind === "invoice" ? "invoiced"
        : mv.kind === "status" ? mv.to : "scheduled", missed, guardNow());
    }
  });

  /**
   * CHANGING WHO IS ON A JOB.
   *
   * Tick the people, and the first one ticked is the lead unless somebody
   * says otherwise — because the common case is one person and asking who
   * leads a crew of one is a question nobody should be made to answer.
   */
  /**
   * CHANGE, AT THE TOP, REACHES THE SAME EDITOR AS THE ONE INSIDE.
   *
   * Two ways to change the crew must not be two editors: one of them would
   * drift and a shop would find out which by putting the wrong person on a
   * job. This scrolls to the existing one and opens it.
   */
  document.getElementById("crew-me")?.addEventListener("click", async () => {
    const b = document.getElementById("crew-me") as HTMLButtonElement;
    b.disabled = true; b.textContent = "adding…";
    try {
      const mine = await meAsTechnician();
      const ids = [...new Set([...onJob.map((t) => t.technician_id), mine])];
      // Lead stays with whoever had it; if nobody did, the person who just
      // put themselves on is the one the desk would ring.
      await setJobTechnicians(id, ids, onJob.find((t) => t.is_lead)?.technician_id ?? mine);
      void jobPage(id, role);
    } catch (e) {
      b.disabled = false; b.textContent = "put me on it";
      const m = document.getElementById("move-msg");
      if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  document.getElementById("whob-edit")?.addEventListener("click", () => {
    /**
     * THE EDITOR LIVES ON THE DETAILS TAB. Anthony, 4 September, on quote
     * 1618 (D8): David "is either missing from the list or won't select".
     * He was on the Money tab: Change opened the picker inside a hidden
     * panel, so nothing appeared and nothing could be ticked. Open the tab
     * the picker is on first, then scroll to it.
     */
    document.querySelector<HTMLButtonElement>('.jtab[data-tab="details"]')?.click();
    const inner = document.getElementById("crew-edit");
    document.getElementById("crew-host")?.scrollIntoView(
      { behavior: "smooth", block: "center" });
    inner?.click();
  });

  /**
   * WHO IS LOOKING AFTER THE CUSTOMER. A short list rather than a sheet:
   * an advisor is one person and picking them is one press.
   */
  document.getElementById("whob-advisor")?.addEventListener("change", async (e) => {
    const sel = e.target as HTMLSelectElement;
    sel.disabled = true;
    try {
      await setAdvisor(id, sel.value || null);
      void jobPage(id, role);
    } catch { sel.disabled = false; }
  });

  document.getElementById("crew-edit")?.addEventListener("click", () => {
    const host = document.getElementById("crew-host");
    if (!host) return;
    if (host.innerHTML.trim()) { host.innerHTML = ""; return; }

    const active = crew.filter((c) => c.active);
    host.innerHTML = `
      <div class="crewpick">
        ${active.map((c) => {
          const on = onJob.find((t) => t.technician_id === c.id);
          return `<label class="check">
            <input type="checkbox" data-tech="${esc(c.id)}"${on ? " checked" : ""}>
            <span>${esc(c.name)}</span>
            <button class="linkbtn crew-lead${on?.is_lead ? " on" : ""}"
                    data-lead="${esc(c.id)}">lead</button>
          </label>`;
        }).join("")}
        <div class="next-do">
          <button class="btn primary" id="crew-save">Save</button>
          <button class="btn ghost" id="crew-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="crew-msg"></div>
      </div>`;

    let lead: string | null = onJob.find((t) => t.is_lead)?.technician_id ?? null;
    host.querySelectorAll<HTMLButtonElement>("[data-lead]").forEach((b) =>
      b.addEventListener("click", (e) => {
        e.preventDefault();
        lead = b.dataset["lead"] ?? null;
        host.querySelectorAll(".crew-lead").forEach((x) => x.classList.remove("on"));
        b.classList.add("on");
        const box = host.querySelector<HTMLInputElement>(`[data-tech="${lead}"]`);
        if (box) box.checked = true;
      }));

    document.getElementById("crew-cancel")?.addEventListener("click", () => { host.innerHTML = ""; });

    document.getElementById("crew-save")?.addEventListener("click", async () => {
      const b = document.getElementById("crew-save") as HTMLButtonElement;
      const m = document.getElementById("crew-msg");
      const picked = Array.from(host.querySelectorAll<HTMLInputElement>("[data-tech]"))
        .filter((x) => x.checked).map((x) => x.dataset["tech"] ?? "");
      b.disabled = true; b.textContent = "Saving…";
      try {
        await setJobTechnicians(id, picked, lead && picked.includes(lead) ? lead : null);
        void jobPage(id, role);
      } catch (e) {
        b.disabled = false; b.textContent = "Save";
        if (m) {
          m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
        }
      }
    });
  });

  /**
   * SEND THE INVOICE — text, email, or both, and say which actually went.
   *
   * "Both" is two sends and either can fail on its own, so the result says
   * what happened to each rather than one cheerful tick covering a text
   * that never left. A customer with no email address is told that before
   * the button is pressed, not after.
   */
  let sendHow: "text" | "email" | "both" = "text";
  document.querySelectorAll<HTMLButtonElement>("#sendhow .chip").forEach((b) =>
    b.addEventListener("click", () => {
      sendHow = (b.dataset["how"] ?? "text") as typeof sendHow;
      document.querySelectorAll("#sendhow .chip").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
    }));

  /* Paid up front: the payment card sits straight under the agreement. */
  if (upFront) {
    const pc = document.getElementById("prepay-card");
    const ec = document.getElementById("esign-card");
    if (pc && ec) ec.after(pc);
  }
  /* NYIA ONLY. Anthony, 2026-10-16: the agreement card on top of every
     Supercanic lead "was only supposed to be on his". A case file sends
     its retainer from here; every other shop sends from Everything else >
     Agreements (agreements.ts). */
  if (shopWords().caseFile) void mountEsign(job!.id);

  document.getElementById("send-invoice")?.addEventListener("click", async () => {
    const b = document.getElementById("send-invoice") as HTMLButtonElement;
    const m = document.getElementById("send-msg");
    const cust = job!.customers;
    const first = (cust?.full_name ?? "").split(" ")[0] || "there";

    const say = (html: string) => { if (m) m.innerHTML = html; };
    // Made on the spot if it is not there. Telling somebody to go and save
    // a record to make a link is a chore the software invented.
    const tok = cust ? await ensurePortalToken(cust.id) : null;
    if (!tok) {
      say(`<p class="msg bad">Could not make a link for this customer.</p>`);
      return;
    }
    if ((sendHow === "email" || sendHow === "both") && !cust?.email) {
      say(`<p class="msg bad">${esc(cust?.full_name ?? "They")} has no email address on file.
        Add one on their record, or send it as a text.</p>`);
      return;
    }
    if ((sendHow === "text" || sendHow === "both") && !cust?.phone) {
      say(`<p class="msg bad">No mobile number on file, so it cannot be texted.</p>`);
      return;
    }

    b.disabled = true; b.textContent = "Sending…";
    const link = `${customerSite()}/garage.html?t=${encodeURIComponent(tok)}`;
    const owed = money$.balance > 0.005 ? money$.balance : total;
    const done: string[] = [];
    const failed: string[] = [];

    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const auth = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      };

      if (sendHow === "text" || sendHow === "both") {
        const r = await fetch(api("/api/sms/send"), {
          method: "POST", headers: auth,
          body: JSON.stringify({
            customer_id: cust!.id, job_id: id, template: true,
            message: `Hi ${first}, here's your invoice from ${shopName()} for ${money(owed)}. ` +
              `You can see it and pay it here: ${link}`,
          }),
        });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(
          r.ok && out.ok ? "texted" : `text failed: ${out.error ?? r.status}`);
      }

      if (sendHow === "email" || sendHow === "both") {
        /**
         * THE EMAIL IS THE INVOICE, not a line pointing at one.
         *
         * This used to send a paragraph with a link in it. An invoice gets
         * forwarded to an accountant and shown to a dealer at trade-in, so
         * it has to survive on its own: the BAR registration, the odometer
         * at the time of service, the plate and VIN, labor and parts split
         * apart, the payment and its reference. The link to the photos and
         * the inspection rides along at the bottom.
         */
        const r = await fetch(api("/api/invoice/send"), {
          method: "POST", headers: auth,
          body: JSON.stringify({ job_id: id }),
        });
        const out = await r.json() as { ok?: boolean; error?: string };
        (r.ok && out.ok ? done : failed).push(
          r.ok && out.ok ? "emailed" : `email failed: ${out.error ?? r.status}`);
      }

      b.disabled = false; b.textContent = "Send it";
      say([
        done.length ? `<p class="msg ok">Sent &mdash; ${esc(done.join(" and "))}.</p>` : "",
        failed.length ? `<p class="msg bad">${esc(failed.join("; "))}</p>` : "",
      ].join(""));
      if (cust) void customerComms("jobcomms", cust.id, cust.phone ?? null);
    } catch (e) {
      b.disabled = false; b.textContent = "Send it";
      say(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
    }
  });

  /**
   * SEND THE QUOTE.
   *
   * There was no way to do this. An advisor could price a job and then had
   * nowhere to press: the only send on the whole card was the review link,
   * which goes out at the other end of the job. So a quote was made and
   * then read out over the phone, or not sent at all.
   *
   * It goes to the customer's own garage page rather than as a wall of
   * text, because that page already shows the lines, the total and an
   * approve button — and because a price in a text is a price with no
   * record of them having seen it.
   */
  /**
   * THE BUTTON. The sending itself moved to data/sendquote.ts so the
   * advisor's walk can press the same one -- Anthony: "send quote is
   * really far down and hard to find, I hope this goes in service advisor
   * wizard." What is left here is what a button does: disable, say what
   * happened, and refresh the conversation underneath.
   */
  const sendQuote = async (btnId: string, msgId: string,
                           via: SendVia = "sms"): Promise<void> => {
    const b = document.getElementById(btnId) as HTMLButtonElement;
    const m = document.getElementById(msgId);
    /* The label to put back on failure, taken off the button rather than
       written here. Three buttons share this function now and they do not
       all say the same thing, so a hardcoded "Send it to them" would
       rename the email button the first time a send failed. */
    const was = b.textContent ?? "Send it to them";
    b.disabled = true; b.textContent = "Sending\u2026";

    const r = await sendTheQuote(job!, lines ?? [], total, () => confirm(
      "This quote already went to them in the last ten minutes. Send it again?"),
      via);
    if (r.ok && ppiJob) await claimPpi(id, "sent the quote");

    if (!r.ok) {
      b.disabled = false; b.textContent = was;
      if (m && r.error) m.innerHTML = `<p class="msg bad">${esc(r.error)}</p>`;
      return;
    }
    b.textContent = "Sent";
    if (m) {
      m.innerHTML = `<p class="msg ok">${
        via === "email"
          ? `Emailed to ${esc(job!.customers?.email ?? "them")}. They can approve it on their own screen.`
          : "Sent. They can approve it on their own screen."}</p>`;
    }
    if (job!.customers) {
      void customerComms("jobcomms", job!.customers.id, job!.customers.phone ?? null);
    }
  };

  /**
   * DRAFTING FROM THE BAND. The estimator lives in the line editor, which
   * is the right place for it once you are editing — but somebody opening
   * a bare quote is looking at the band, so the band offers it too and
   * simply scrolls them to the thing that does it.
   */
  document.getElementById("draft-quote-2")?.addEventListener("click", () => {
    const b = document.getElementById("draftquote");
    // The draft folds away once a quote has lines. Open it first, so the
    // text it writes lands somewhere somebody can see.
    const fold = document.getElementById("draftfold") as HTMLDetailsElement | null;
    if (fold) fold.open = true;
    document.getElementById("lineedit")?.scrollIntoView({ behavior: "smooth", block: "center" });
    b?.click();
  });

  /**
   * CANCELLING A QUOTE IS A REAL ANSWER and it was hidden in a status
   * chip. A quote nobody is going to accept should be closed, not left
   * on the board pretending to be live work — the leads screen already
   * exists because of exactly that shape.
   *
   * It asks why, and the reason goes on the record. "Cancelled" with no
   * sentence is the thing somebody finds in three months and cannot use.
   */
  document.getElementById("cancel-quote")?.addEventListener("click", async () => {
    /**
     * A NO IS WORTH ONE QUESTION FIRST.
     *
     * Anthony: "allow to close open estimates when a customer turns them
     * down. Maybe ask if they want a modification instead of just a no?"
     *
     * Most of the time it is not a no, it is a no AT THAT PRICE. A customer
     * who baulks at a $900 brake job will often take the pads and leave the
     * rotors, and cancelling the quote loses the job, the conversation and
     * the record that they ever asked. The quote state machine has always
     * had "changes" for this and nothing in the interface ever set it, so
     * the only button an advisor had said Cancel.
     *
     * So the button asks which of the two this is, and only one of the
     * answers throws the work away. Changes keeps it an open estimate,
     * marks it as theirs to answer, and it reappears in the quote pile
     * under "Changes asked for" rather than vanishing.
     */
    const wantsChanges = confirm(
      "Did they turn it down, or do they want it changed?\n\n"
      + "OK  — they want it changed. The quote stays open and goes back on "
      + "the pile to re-price.\n"
      + "Cancel — they turned it down, and I will ask you why.");

    if (wantsChanges) {
      const b0 = document.getElementById("cancel-quote") as HTMLButtonElement;
      b0.disabled = true; b0.textContent = "Marking…";
      try {
        await updateJobField(id, "changes_requested_at", new Date().toISOString());
        void jobPage(id, role);
      } catch (e) {
        b0.disabled = false; b0.textContent = "Cancel this quote";
        const m0 = document.getElementById("send-quote-msg");
        if (m0) m0.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
      return;
    }

    const why = prompt(
      "They turned it down.\n\nWhy, for the record — went elsewhere, changed "
      + "their mind, cannot reach them:", "");
    if (why === null) return;
    const b = document.getElementById("cancel-quote") as HTMLButtonElement;
    const m = document.getElementById("send-quote-msg");
    b.disabled = true; b.textContent = "Cancelling…";
    try {
      await setStatus(id, "cancelled",
        { hasAuthorization: live.length > 0, hasPricedLine: lines!.some((l) => l.total > 0) },
        why.trim() || "Cancelled from the quote screen.");
      void jobPage(id, role);
    } catch (e) {
      b.disabled = false; b.textContent = "Cancel this quote";
      if (m) {
        m.innerHTML = `<p class="msg bad">${
          esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    }
  });

  /* WHY, IN THEIR OWN WORDS. The prompt offers the four that come up, and
     takes anything, because the fifth reason is the one nobody predicted. */
  document.getElementById("to-nocharge")?.addEventListener("click", async () => {
    const why = prompt(
      "Why is this one not being billed?\n\n"
      + "Warranty, goodwill, our mistake, or it turned out to be five "
      + "minutes. Whatever you type is what the job will say.", "Warranty");
    if (why === null || !why.trim()) return;
    try {
      await setNoCharge(id, why);
      void jobPage(id, role);
    } catch (e) {
      const m = document.getElementById("move-msg");
      if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  document.getElementById("charge-after-all")?.addEventListener("click", async () => {
    if (!confirm("Put this job back to being billed?")) return;
    try { await setNoCharge(id, null); void jobPage(id, role); }
    catch (e) {
      const m = document.getElementById("move-msg");
      if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  /* Straight to the quote from the warning, so noticing the mistake and
     fixing it are the same click. */
  document.getElementById("cp-to-quote")?.addEventListener("click", () => {
    chosenTab.set(id, "work");
    void jobPage(id, role);
  });

  /* Raise the bill from the warning, for the same reason: the advisor is
     already here with the link open, and sending them off to another tab
     to find the button is how it gets sent unbilled a second time. */
  document.getElementById("cp-to-bill")?.addEventListener("click", async () => {
    const b = document.getElementById("cp-to-bill") as HTMLButtonElement;
    const said = document.getElementById("cp-said");
    b.disabled = true;
    b.textContent = "Raising it…";
    try {
      await raiseInvoice(id);
      if (!await closedByPrepayment(id)) await setStatus(id, "invoiced", guardNow());
      void jobPage(id, role);
    } catch (e) {
      b.disabled = false;
      b.textContent = "raise the invoice first";
      if (said) {
        said.innerHTML = `<p class="msg bad"></p>`;
        said.firstElementChild!.textContent =
          e instanceof Error ? e.message : String(e);
      }
    }
  });

  /* The band's one button takes whichever road this customer actually has.
     With a number it texts, exactly as it always did; with only an address
     it emails, which is the case that used to render no button at all. */
  /* THE CLIENT'S OWN QUOTE, PRINTED BEFORE IT IS SENT. Laura at NYIA,
     2026-10-16. The window opens on the click so no blocker eats it, and
     is filled once the server has built the same document the email is. */
  document.getElementById("print-quote")?.addEventListener("click", async () => {
    const b = document.getElementById("print-quote") as HTMLButtonElement;
    const win = window.open("", "_blank");
    b.disabled = true; b.textContent = "Getting it ready…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/quote/preview"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: JSON.stringify({ job_id: id }),
      });
      const d = await res.json() as { html?: string; subject?: string; error?: string };
      if (!res.ok || !d.html) throw new Error(d.error ?? "That did not work.");
      if (!win) throw new Error("Your browser blocked the print window. Allow pop-ups for this site and try again.");
      win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.subject ?? "Quote")}</title></head>`
        + `<body style="margin:0">${d.html}</body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
      b.disabled = false; b.textContent = "Print the quote";
    } catch (e) {
      win?.close();
      b.disabled = false; b.textContent = "Print the quote";
      const m = document.getElementById("send-quote-msg");
      if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  document.getElementById("send-quote")
    ?.addEventListener("click", () => void sendQuote("send-quote", "move-msg",
      job?.customers?.phone ? "sms" : "email"));

  if (document.getElementById("ppi-slot")) {
    void maySendToPpiDesk().then((yes) => {
      const slot = document.getElementById("ppi-slot");
      if (!yes || !slot) return;
      slot.outerHTML = `<button class="btn" id="to-ppidesk">Send to PPI Desk</button>`;
      wireSendToPpiDesk(document.getElementById("to-ppidesk") as HTMLButtonElement | null, { job_id: id },
        (t, bad) => {
          const m = document.getElementById("move-msg");
          if (m) m.innerHTML = `<p class="msg${bad ? " bad" : ""}">${esc(t)}</p>`;
        });
    });
  }
  document.getElementById("send-quote-2")
    ?.addEventListener("click", () => void sendQuote("send-quote-2", "send-quote-msg"));
  document.getElementById("email-quote")
    ?.addEventListener("click", () => void sendQuote("email-quote", "send-quote-msg", "email"));

  /**
   * ADD THE CAR, on a job that has none.
   *
   * The plate and VIN fields edit the vehicle RECORD, so on a job with no
   * vehicle attached they had nothing to write to and there was no button
   * anywhere that made one. An advisor drafting a quote off a phone call
   * hit that every single time.
   *
   * Year, make and model, with the VIN first because one paste fills the
   * other three — the same shape as the lead sheet, so it is the same
   * thing to learn twice rather than two things once.
   */
  document.getElementById("add-car")?.addEventListener("click", () => {
    const host = document.getElementById("add-car-host");
    if (!host || host.querySelector("form")) return;
    /**
     * ─── THE LAST BLOCK IN THIS FILE STILL SPEAKING CAR ────────────────
     *
     * Anthony, 2026-09-18: "they still have vehicle or vin on their start
     * job tab. Change to location or something that fits their business."
     *
     * Everything else on this page had been moved onto the vocabulary and
     * this form had not, so a private investigator opening a case was
     * asked for a Year, a Make and a Model. It reads Year opened, Matter
     * and Work for them now, and Year, Make and Model for a garage --
     * same fields, same columns, per-trade words.
     *
     * THE DECODER IS GATED, NOT RELABELLED. Three of these were a VIN
     * problem rather than a wording one:
     *
     *   - Decode sends the box to a VIN database. A case number typed in
     *     and decoded returns nothing, and the button implies the firm's
     *     own file numbers are something we can look up.
     *   - maxlength 17 is the length of a VIN. A longer case number was
     *     silently truncated at the seventeenth character, which is worse
     *     than refusing it.
     *   - autocapitalize="characters" shouts a case number into caps.
     *
     * So the trade that has an engine keeps all three and nobody else
     * gets any of them. The same test guards the decode link on the
     * identity card above.
     *
     * AND A TRADE WITH A PLATE. An RV has no engine field and does have a
     * registered VIN, which the same federal database decodes -- and the
     * walkthrough already tells every plate-carrying trade to "paste the
     * VIN and press decode" (src/ui/words.ts). A plate is the sign that
     * the serial is a road vehicle's VIN rather than a shop's own number.
     */
    const serialIsVin = w.hasEngine || w.hasPlate;
    host.innerHTML = `
      <form class="addcar" id="addcar">
        <label class="tocar-f wide"><span>${esc(cap(w.serial))}</span>
          <span class="${serialIsVin ? "tocar-vin" : ""}">
            <input id="ac-vin" spellcheck="false"${
              serialIsVin ? ` autocapitalize="characters" maxlength="17"` : ""}>
            ${serialIsVin
              ? `<button type="button" class="btn sm" id="ac-decode">Decode</button>` : ""}
          </span></label>
        <div id="ac-vinout"></div>
        <div class="tocar-row">
          <label class="tocar-f"><span>${esc(w.yearLabel)}</span>
            <input id="ac-year" inputmode="numeric" maxlength="4"></label>
          <label class="tocar-f"><span>${esc(w.makeLabel)}</span><input id="ac-make"></label>
          <label class="tocar-f"><span>${esc(w.modelLabel)}</span><input id="ac-model"></label>
        </div>
        <!-- A door has no plate. Not offered rather than offered blank. -->
        ${w.hasPlate ? `<label class="tocar-f wide"><span>Plate</span>
          <input id="ac-plate" autocapitalize="characters"></label>` : ""}
        <div class="next-do">
          <button type="button" class="btn primary" id="ac-save">Add it</button>
          <button type="button" class="btn ghost" id="ac-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="ac-msg"></div>
      </form>`;

    const v = (x: string) =>
      (document.getElementById(x) as HTMLInputElement | null)?.value.trim() ?? "";

    document.getElementById("ac-cancel")?.addEventListener("click", () => { host.innerHTML = ""; });

    document.getElementById("ac-decode")?.addEventListener("click", async () => {
      const out = document.getElementById("ac-vinout");
      const db2 = document.getElementById("ac-decode") as HTMLButtonElement;
      const vin = v("ac-vin").toUpperCase();
      if (!out) return;
      if (vin.length !== 17) {
        out.innerHTML = `<p class="msg bad">A VIN is 17 characters. That one is ${vin.length}.</p>`;
        return;
      }
      db2.disabled = true; db2.textContent = "Reading…";
      try {
        const r = await fetch(api(`/api/vin?vin=${encodeURIComponent(vin)}`));
        const d = await r.json() as Record<string, string | number | null>;
        db2.disabled = false; db2.textContent = "Decode";
        if (!r.ok) {
          out.innerHTML = `<p class="msg bad">${esc(String(d["error"] ?? "No."))}</p>`;
          return;
        }
        const put = (x: string, val: unknown) => {
          const el = document.getElementById(x) as HTMLInputElement | null;
          if (el && !el.value.trim() && val) el.value = String(val);
        };
        put("ac-year", d["year"]); put("ac-make", d["make"]); put("ac-model", d["model"]);
        out.innerHTML = vinOutHtml(d, { bits: false });
      } catch {
        db2.disabled = false; db2.textContent = "Decode";
        out.innerHTML = `<p class="msg bad">The lookup did not answer.</p>`;
      }
    });

    document.getElementById("ac-save")?.addEventListener("click", async () => {
      const b = document.getElementById("ac-save") as HTMLButtonElement;
      const m = document.getElementById("ac-msg");
      const yr = Number(v("ac-year"));
      if (!v("ac-make") && !v("ac-plate") && !v("ac-vin")) {
        /* The refusal has to name the boxes on the screen. "Give it a
           make" in front of a form whose fields read Matter and Work
           tells somebody to fill in something that is not there. */
        if (m) {
          m.innerHTML = `<p class="msg bad">Give it a ${esc(w.makeLabel.toLowerCase())}${
            w.hasPlate ? ", a plate" : ""} or a ${esc(w.serial)} at least.</p>`;
        }
        return;
      }
      b.disabled = true; b.textContent = "Adding…";
      try {
        await attachVehicle(id, {
          year: yr >= 1900 && yr <= 2100 ? yr : null,
          make: v("ac-make") || null, model: v("ac-model") || null,
          license_plate: v("ac-plate") || null, vin: v("ac-vin").toUpperCase() || null,
        });
        void jobPage(id, role);
      } catch (e) {
        b.disabled = false; b.textContent = "Add it";
        if (m) {
          m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
        }
      }
    });
  });

  /**
   * ON MY WAY. Not a stage — a message. It is the thing a customer waiting
   * at home most wants and least often gets, and it is one press because
   * anything longer does not happen from a driver's seat.
   *
   * The text says a name and a time and nothing else. It is written down
   * on the job the same as any other send, so the next person to open the
   * card can see the customer was told.
   */
  document.getElementById("omw")?.addEventListener("click", async () => {
    const b = document.getElementById("omw") as HTMLButtonElement;
    const m = document.getElementById("move-msg");
    const first = (job!.customers?.full_name ?? "").split(" ")[0] || "there";
    b.disabled = true; b.textContent = "Sending…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/sms/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          customer_id: job!.customers!.id,
          job_id: id,
          template: true,
          message: `Hi ${first}, on my way to you now. See you shortly.`,
        }),
      });
      const out = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !out.ok) {
        b.disabled = false; b.textContent = "On my way";
        if (m) m.innerHTML = `<p class="msg bad">${esc(out.error ?? "That did not go.")}</p>`;
        return;
      }
      b.textContent = "Told them";
      if (m) m.innerHTML = `<p class="msg ok">Sent.</p>`;
      /* THEY HAVE TOLD THE CUSTOMER, SO THE NEXT THING IS THE JOB.
         Anthony: "when they click 'on the way' then they get 'start the
         job'". The strip stops leading with On my way and leads with the
         walk instead, which is the only thing left to do. */
      document.getElementById("mechstrip")?.classList.add("told");
      if (job!.customers) {
        void customerComms("jobcomms", job!.customers.id, job!.customers.phone ?? null);
      }
    } catch (e) {
      b.disabled = false; b.textContent = "On my way";
      if (m) {
        m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    }
  });

  document.querySelectorAll<HTMLButtonElement>("#stagepick .chip").forEach((b) =>
    b.addEventListener("click", async () => {
      const to = b.dataset["st"] as Status;
      const guard = guardNow();
      try {
        await setStatus(id, to, guard);
        void jobPage(id, role);
      } catch (e) {
        const missed = (e as Error & { protocol?: string[] }).protocol;
        // A plain failure is just a failure. A PROTOCOL failure is the
        // system doing its job, and what happens next depends on who is
        // standing there.
        if (!missed) { say(e); return; }
        askOverride(to, missed, guard);
      }
    }));

  /**
   * The prompt. A technician gets told what is missing and is expected to
   * go and do it — being walked through the gap IS the check working.
   * Somebody senior gets the same list plus a way past it that costs them
   * a sentence, and that sentence lands on the job for good.
   */
  function askOverride(to: Status, missed: string[], guard: { hasAuthorization: boolean; hasPricedLine: boolean }): void {
    const may = canOverrideProtocol(role);
    const m = document.getElementById("stage-msg");
    if (!m) return;
    m.innerHTML = `
      <div class="pgate">
        <div class="pgate-h">${esc(STATUS_LABEL[to] ?? to)} is missing ${missed.length === 1 ? "something" : "some things"}</div>
        <ul class="pgate-list">${missed.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>
        <div class="fine">${esc(overrideHint(role))}</div>
        ${may ? `
          <div class="fine">Why are you closing it anyway?</div>
          <div class="pgate-act">
            <input id="ovr" maxlength="240">
            <button class="btn" id="ovr-go">Close it anyway</button>
          </div>` : ""}
      </div>`;

    document.getElementById("ovr-go")?.addEventListener("click", async () => {
      const why = (document.getElementById("ovr") as HTMLInputElement).value.trim();
      if (!why) {
        // No reason, no override. The record is the entire point of it.
        m.querySelector(".fine")!.textContent = "Give a reason first — it is what makes this defensible later.";
        return;
      }
      try { await setStatus(id, to, guard, why); void jobPage(id, role); }
      catch (e2) { say(e2); }
    });
  }

  const saveAppt = async () => {
    const dateBox = document.getElementById("appt-date") as HTMLInputElement | null;
    const timeBox = document.getElementById("appt-time") as HTMLInputElement | null;
    const b = document.getElementById("appt-save") as HTMLButtonElement | null;
    if (!dateBox || !timeBox) return;
    const d = dateBox.value.trim(), t = timeBox.value.trim();
    if (d && !t) { say(new Error("Pick a time as well as the date.")); return; }
    if (b) { b.disabled = true; b.textContent = "Saving…"; }
    try {
      await setScheduledAt(id, d ? `${d}T${t}` : "");
      if (ppiJob && d) await claimPpi(id, "booked it");
      void jobPage(id, role);
    } catch (err) {
      if (b) { b.disabled = false; b.textContent = "Set the time"; }
      say(err);
    }
  };
  document.getElementById("appt-save")?.addEventListener("click", () => void saveAppt());
  for (const k of ["appt-date", "appt-time"]) {
    document.getElementById(k)?.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); void saveAppt(); }
    });
  }

  document.querySelectorAll<HTMLButtonElement>("#where-pick .chip").forEach((b) =>
    b.addEventListener("click", async () => {
      const at = b.dataset["at"] as "mobile" | "shop";
      // Pressing the one that is already on clears it, so "not said" stays
      // reachable rather than becoming a one-way door.
      await setServiceAt(job!.id, job!.service_at === at ? null : at).catch(() => {});
      void jobPage(id, role);
    }));

  // Only where it is the next thing to do.
  // The VIN says what the car legally is. Offered, never applied — a VIN
  // that decodes to a different vehicle than the one on the job usually
  // means a typo and occasionally means the car is not what somebody said.
  document.getElementById("vin-go")?.addEventListener("click", async () => {
    const out = document.getElementById("vin-out");
    const b = document.getElementById("vin-go") as HTMLButtonElement;
    if (!out) return;
    b.disabled = true; b.textContent = "decoding…";
    try {
      const r = await fetch(api(`/api/vin?vin=${encodeURIComponent(job!.vehicles!.vin!)}`));
      const d = await r.json() as Record<string, unknown>;
      b.disabled = false; b.textContent = "decode";
      if (!r.ok) { out.innerHTML = `<p class="msg bad">${esc(String(d["error"]))}</p>`; return; }
      out.innerHTML = vinOutHtml(d);
    } catch (e) {
      b.disabled = false; b.textContent = "decode";
      out.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  // What was said, on the job itself. It was only ever on the customer,
  // which meant answering "did anyone tell them about the extra hour"
  // required leaving the job you were looking at.
  if (job.customers) {
    void customerComms("jobcomms", job.customers.id, job.customers.phone ?? null);
  } else {
    const h = document.getElementById("jobcomms");
    if (h) h.remove();
  }

  document.getElementById("ask-review")?.addEventListener("click", async () => {
    const b = document.getElementById("ask-review") as HTMLButtonElement;
    const out = document.getElementById("review-out");
    const first = (job!.customers?.full_name ?? "").split(" ")[0] || "there";
    const link = reviewPick(job!).url;
    if (!link) return;
    b.disabled = true; b.textContent = "Sending…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/sms/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          customer_id: job!.customers!.id,
          job_id: id,
          template: true,
          // Anthony's voice: short, one ask, no sales copy.
          message: `Thanks ${first}, appreciate the business. ` +
            `If we did right by you a quick review helps us a lot: ${link}`,
        }),
      });
      const body = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !body.ok) {
        b.disabled = false; b.textContent = "Send the review link";
        if (out) out.innerHTML = `<p class="msg bad">${esc(body.error ?? "That did not go.")}</p>`;
        return;
      }
      if (out) out.innerHTML = `<p class="msg ok">Sent.</p>`;
      b.textContent = "Sent";
      void customerComms("jobcomms", job!.customers!.id, job!.customers!.phone ?? null);
    } catch (e) {
      b.disabled = false; b.textContent = "Send the review link";
      if (out) out.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
    }
  });

  /* Notes on the job, under History. Only the job: a note about the car
     itself belongs on the car and a note about the person belongs on their
     record, and pulling either in here would bury today under everything
     that was ever true. */
  void notesCard("job-notes", { jobId: id }, role, {
    title: "Notes", blurb: "What you were told, or what you found" });

  /* FOLDED SHUT ON LOAD, ALWAYS. The card now lives under the six tabs, above
     every panel, so it is found before any tab is opened. On load it shows
     just its header row — the count and the one-tap camera — and the gallery
     is a tap away; the same on a quote and on a job in the bay. */
  jobPhotos("jobphotos", id, job.vehicles?.id ?? null, role, "damage",
    { shut: true });

  if (needsAuth) {
    /* The REPAIR total, not the job total. Storage and towing are not
       things a customer authorizes here. See 0119. */
    /* Every paper on this job with money on it, so a tow can be agreed
       on its own rather than swept into the repair yes. See 0119. */
    const papers = [
      { paper: "repair" as const, amount: job.totals.repair_total },
      ...separatePapersOn(lines ?? []).map((p) => ({
        paper: p,
        amount: linesFor(lines ?? [], p)
          .reduce((n, l) => n + Number(l.total ?? 0), 0),
      })),
    ].filter((p) => p.amount > 0);
    authForm("authhost", job.id, job.totals.repair_total,
      job.customers?.full_name ?? "", () => void jobPage(id, role), papers, visitQuote);
  }

  /* The teardown form opens on a press rather than sitting open, because
     most jobs never have one and an always-open form with five money
     boxes on it reads as five things somebody forgot to fill in. */
  document.getElementById("td-open")?.addEventListener("click", () => {
    const btn = document.getElementById("td-open");
    if (btn) btn.hidden = true;
    teardownForm("td-host", job.id, job.customers?.full_name ?? "",
      () => void jobPage(id, role));
  });
}
